import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FaArchive,
  FaBell,
  FaBook,
  FaBookOpen,
  FaBuilding,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaClone,
  FaCog,
  FaDatabase,
  FaExchangeAlt,
  FaFileAlt,
  FaFileContract,
  FaLock,
  FaMapMarkedAlt,
  FaMoneyBillWave,
  FaPlus,
  FaRegCalendarAlt,
  FaRegSave,
  FaSchool,
  FaTable,
  FaTasks,
  FaUniversity,
  FaUserGraduate,
  FaUsers,
  FaWrench,
} from 'react-icons/fa';
import { axios } from '../../context/AuthContext';
import asderLogo from '../../images/asderrLogo.png';

const API = '/haftalik-program';
const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const DAY_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const COLORS = ['#2563EB', '#E8334A', '#10B981', '#F59E0B', '#7C3AED', '#0891B2', '#DB2777', '#475569', '#65A30D', '#EA580C'];

const emptyForm = {
  subject: { name: '', shortName: '', weeklyHours: 2, distributionFormat: '1+1', color: COLORS[0] },
  teacher: { name: '', shortName: '', branch: '', homeroomClass: '', club: '', email: '' },
};

const valueOf = (obj, keys, fallback = '') => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== '') return obj[key];
  }
  return fallback;
};

const getPayload = (response) => response?.data?.data ?? response?.data ?? {};
const asArray = (value) => Array.isArray(value) ? value : [];
const slotKey = (day, period) => `${day}_${period}`;

function sumDistributionFormat(value) {
  const clean = String(value || '').replace(/\s+/g, '');
  if (!/^\d+(\+\d+)*$/.test(clean)) return null;
  return clean.split('+').map(Number).reduce((total, part) => total + part, 0);
}

function extractTimetable(payload) {
  return normalizeTimetable(payload?.timetableResult || payload?.timetable_result || payload?.program || payload);
}

function extractPendingLessons(payload) {
  return asArray(payload?.pendingLessons || payload?.pending_lessons || payload?.bekleyenDersler || payload?.lessons || payload);
}

function normalizeAvailability(items) {
  const map = {};
  asArray(items).forEach((item) => {
    const key = valueOf(item, ['slotKey', 'slot_key', 'key'], '');
    if (!key) return;
    const rawStatus = String(valueOf(item, ['status', 'type', 'color', 'durum'], '')).toLowerCase();
    let status = rawStatus;
    if (item.available === true || item.isAvailable === true || rawStatus === 'available' || rawStatus === 'uygun') status = 'green';
    if (rawStatus === 'busy' || rawStatus === 'teacher_busy' || rawStatus === 'black' || rawStatus === 'dolu') status = 'black';
    if (rawStatus === 'blocked' || rawStatus === 'rule' || rawStatus === 'unavailable' || rawStatus === 'red' || rawStatus === 'kapali') status = 'red';
    map[key] = {
      ...item,
      status: ['green', 'black', 'red'].includes(status) ? status : 'red',
      message: valueOf(item, ['message', 'reason', 'aciklama'], ''),
    };
  });
  return map;
}

function buildDefaultSettings() {
  return DAY_NAMES.map((name, dayIndex) => ({
    dayIndex,
    name,
    shortName: DAY_SHORT[dayIndex],
    isActive: dayIndex < 5,
    periodCount: dayIndex < 5 ? 8 : 4,
    timeSlots: Array.from({ length: 18 }, (_, i) => ({
      start: i < 8 ? `${String(9 + Math.floor(i * 45 / 60)).padStart(2, '0')}:${String((i * 45) % 60).padStart(2, '0')}` : '00:00',
      end: i < 8 ? `${String(9 + Math.floor((i * 45 + 40) / 60)).padStart(2, '0')}:${String((i * 45 + 40) % 60).padStart(2, '0')}` : '00:00',
    })),
  }));
}

function normalizeSettings(raw) {
  const source = raw?.dailyConfigs || raw?.daily_configs || raw?.gunler || raw?.days || raw;
  if (!Array.isArray(source) || source.length === 0) return buildDefaultSettings();
  const byIndex = {};
  source.forEach((day, index) => {
    const dayIndex = Number(valueOf(day, ['dayIndex', 'day_index', 'gunIndex', 'gun_index'], index));
    byIndex[dayIndex] = day;
  });
  return buildDefaultSettings().map((fallback) => {
    const day = byIndex[fallback.dayIndex] || {};
    const rawSlots = asArray(valueOf(day, ['timeSlots', 'time_slots', 'saatler', 'slots'], []));
    const periodCount = Math.max(1, Math.min(18, Number(valueOf(day, ['periodCount', 'period_count', 'dersSayisi', 'slotCount'], rawSlots.length || fallback.periodCount))));
    return {
      dayIndex: fallback.dayIndex,
      name: valueOf(day, ['name', 'ad', 'gunAdi'], fallback.name),
      shortName: valueOf(day, ['shortName', 'short_name', 'kisaAd'], fallback.shortName),
      isActive: Boolean(valueOf(day, ['isActive', 'is_active', 'aktif'], fallback.isActive)),
      periodCount,
      timeSlots: Array.from({ length: 18 }, (_, i) => {
        const slot = rawSlots[i] || fallback.timeSlots[i] || {};
        return {
          start: valueOf(slot, ['start', 'baslangic', 'bas', 'startTime'], '00:00'),
          end: valueOf(slot, ['end', 'bitis', 'bit', 'endTime'], '00:00'),
        };
      }),
    };
  });
}

function normalizeSubjects(items) {
  return asArray(items).map((item, index) => {
    const weeklyHours = Number(valueOf(item, [
      'weeklyHours',
      'weekly_hours',
      'haftalikDersSaati',
      'haftalik_ders_saati',
      'haftalikSaat',
      'haftalik_saat',
      'HDS',
      'hds',
    ], 0));
    const distributionFormat = valueOf(item, [
      'distributionFormat',
      'distribution_format',
      'placementFormat',
      'placement_format',
      'yerlesimBicimi',
      'yerlesim_bicimi',
      'yerleşimBiçimi',
      'yerlesmeBicimi',
      'yerlesme_bicimi',
      'yerleşmeBiçimi',
      'yerBicimi',
      'yer_bicimi',
      'yerlesim',
      'yer_bic',
      'yerBic',
      'yerBiç',
    ], weeklyHours > 0 ? String(weeklyHours) : '1');

    return {
      ...item,
      id: valueOf(item, ['id', '_id', 'subjectId', 'ders_id'], index + 1),
      name: valueOf(item, ['name', 'ad', 'dersAdi', 'ders_adi', 'subjectName'], ''),
      shortName: valueOf(item, ['shortName', 'short_name', 'kisaAd', 'kisa_ad', 'kod'], ''),
      weeklyHours,
      distributionFormat,
      color: valueOf(item, ['color', 'renk'], COLORS[index % COLORS.length]),
    };
  });
}

function normalizeClasses(items) {
  return asArray(items).map((item, index) => ({
    ...item,
    id: valueOf(item, ['id', '_id', 'classId', 'sinif_id'], index + 1),
    name: valueOf(item, ['name', 'ad', 'sinifAdi', 'sinif_adi'], ''),
    order: Number(valueOf(item, ['order', 'sira'], index + 1)),
  }));
}

function normalizeTeachers(items) {
  return asArray(items).map((item, index) => {
    const fullName = valueOf(item, ['name', 'adSoyad', 'ad_soyad'], `${valueOf(item, ['ad'], '')} ${valueOf(item, ['soyad'], '')}`.trim());
    return {
      ...item,
      id: valueOf(item, ['id', '_id', 'teacherId', 'ogretmen_id'], index + 1),
      name: fullName,
      shortName: valueOf(item, ['shortName', 'short_name', 'kisaAd', 'kisa_ad'], fullName.split(' ').map(part => part[0]).join('').slice(0, 3).toUpperCase()),
      branch: valueOf(item, ['branch', 'brans', 'branş'], ''),
      homeroomClass: valueOf(item, ['homeroomClass', 'sinifOgretmeni', 'sinif_ogretmeni'], ''),
      club: valueOf(item, ['club', 'sosyalKulubu', 'sosyal_kulubu'], ''),
      email: valueOf(item, ['email', 'eposta'], ''),
      unavailableSlots: asArray(valueOf(item, ['unavailableSlots', 'unavailable_slots', 'kapaliSaatler', 'kapali_saatler'], [])),
    };
  });
}

function normalizeAssignments(items) {
  return asArray(items).map((item, index) => ({
    ...item,
    id: valueOf(item, ['id', '_id'], index + 1),
    teacherId: String(valueOf(item, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')),
    classId: String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')),
    subjectId: String(valueOf(item, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')),
    weeklyHours: Number(valueOf(item, ['weeklyHours', 'weekly_hours', 'haftalikSaat', 'haftalik_saat', 'hds'], 0)),
  }));
}

function normalizeClassSubjects(items) {
  return asArray(items).map((item, index) => ({
    ...item,
    id: valueOf(item, ['id', '_id'], index + 1),
    classId: String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')),
    subjectId: String(valueOf(item, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '')),
    weeklyHours: Number(valueOf(item, ['weeklyHours', 'weekly_hours', 'haftalikSaat', 'hds'], 0)),
    distributionFormat: valueOf(item, [
      'distributionFormat',
      'distribution_format',
      'placementFormat',
      'placement_format',
      'yerlesimBicimi',
      'yerlesim_bicimi',
      'yerleşimBiçimi',
      'yerlesmeBicimi',
      'yerlesme_bicimi',
      'yerleşmeBiçimi',
      'yerBicimi',
      'yer_bicimi',
      'yerlesim',
      'yer_bic',
      'yerBic',
      'yerBiç',
    ], ''),
  }));
}

function normalizeTimetable(raw) {
  const timetable = raw?.timetableResult || raw?.timetable_result || raw?.program || raw || {};
  return {
    ...timetable,
    assignments: timetable?.assignments && typeof timetable.assignments === 'object' ? timetable.assignments : {},
  };
}

function entityName(map, id, fallback = '-') {
  return map[String(id)]?.name || fallback;
}

function entityShort(map, id, fallback = '-') {
  return map[String(id)]?.shortName || map[String(id)]?.name || fallback;
}

function WindowModal({ title, width = 980, children, onClose }) {
  return (
    <div className="hp-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="hp-window" style={{ maxWidth: width }}>
        <div className="hp-window-titlebar">
          <div>
            <strong>{title}</strong>
            <span>Haftalık program yönetimi</span>
          </div>
          <button className="hp-icon-btn" type="button" onClick={onClose}>x</button>
        </div>
        <div className="hp-window-body">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="hp-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function DenseTable({ headers, children, minWidth = 620 }) {
  return (
    <div className="hp-table-wrap">
      <table className="hp-table" style={{ minWidth }}>
        <thead>
          <tr>{headers.map((head) => <th key={head}>{head}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export default function DersProgramiPage() {
  const [loading, setLoading] = useState(true);
  const [activeWindow, setActiveWindow] = useState(null);
  const [viewMode, setViewMode] = useState('class');
  const [selectedId, setSelectedId] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [pendingLessons, setPendingLessons] = useState([]);
  const [selectedPendingLesson, setSelectedPendingLesson] = useState(null);
  const [availabilityMap, setAvailabilityMap] = useState({});

  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [emptyClassSubjects, setEmptyClassSubjects] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [settings, setSettings] = useState(buildDefaultSettings);
  const [dailyLimits, setDailyLimits] = useState([]);
  const [rules, setRules] = useState([]);
  const [timetableResult, setTimetableResult] = useState({ assignments: {} });

  const loadSetup = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/setup`);
      const payload = data?.data ?? data ?? {};
      setSubjects(normalizeSubjects(payload.subjects || payload.dersler));
      setClasses(normalizeClasses(payload.classes || payload.siniflar));
      setTeachers(normalizeTeachers(payload.teachers || payload.ogretmenler));
      setClassSubjects(normalizeClassSubjects(payload.classSubjects || payload.sinifDersleri || payload.sinif_dersleri));
      setEmptyClassSubjects(
        payload.emptyClassSubjects || payload.bosDersler
          ? normalizeClassSubjects(payload.emptyClassSubjects || payload.bosDersler)
          : null
      );
      setAssignments(normalizeAssignments(payload.assignments || payload.atamalar));
      setSettings(normalizeSettings(payload.settings || payload.ayarlar || payload.dailyConfigs || payload.daily_configs));
      setDailyLimits(asArray(payload.dailyLimits || payload.gunlukDersSayilari || payload.classDailyLimits));
      setRules(asArray(payload.rules || payload.kurallar));
      setTimetableResult(normalizeTimetable(payload.timetableResult || payload.program || payload.liste));
      setWarnings(asArray(payload.warnings || payload.uyarilar));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Haftalık program verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  const subjectMap = useMemo(() => Object.fromEntries(subjects.map(item => [String(item.id), item])), [subjects]);
  const classMap = useMemo(() => Object.fromEntries(classes.map(item => [String(item.id), item])), [classes]);
  const teacherMap = useMemo(() => Object.fromEntries(teachers.map(item => [String(item.id), item])), [teachers]);
  const activeDays = useMemo(() => settings.filter(day => day.isActive), [settings]);
  const maxPeriod = useMemo(() => Math.max(1, ...activeDays.map(day => day.periodCount || 1)), [activeDays]);
  const periods = useMemo(() => Array.from({ length: maxPeriod }, (_, i) => i), [maxPeriod]);

  const visibleList = viewMode === 'teacher' ? teachers : viewMode === 'subject' ? subjects : classes;
  const resolvedSelectedId = selectedId || String(visibleList[0]?.id || '');

  useEffect(() => {
    setSelectedId('');
    setSelectedCell(null);
    setSelectedPendingLesson(null);
    setAvailabilityMap({});
  }, [viewMode]);

  const loadPendingLessons = useCallback(async (classId) => {
    if (!classId) {
      setPendingLessons([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/bekleyen-dersler`, { params: { classId } });
      const payload = getPayload(response);
      setPendingLessons(extractPendingLessons(payload));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bekleyen dersler alınamadı');
    }
  }, []);

  const getCellForSlot = useCallback((day, period) => {
    const key = slotKey(day, period);
    if (viewMode === 'class') {
      const cell = timetableResult.assignments?.[String(resolvedSelectedId)]?.[key];
      return cell ? { classId: String(resolvedSelectedId), key, cell } : null;
    }
    for (const [classId, slots] of Object.entries(timetableResult.assignments || {})) {
      const cell = slots?.[key];
      if (!cell) continue;
      const teacherIds = asArray(cell.teacherIds).map(String);
      if (cell.teacherId) teacherIds.push(String(cell.teacherId));
      if (viewMode === 'teacher' && teacherIds.includes(String(resolvedSelectedId))) return { classId, key, cell };
      if (viewMode === 'subject' && String(cell.subjectId) === String(resolvedSelectedId)) return { classId, key, cell };
    }
    return null;
  }, [resolvedSelectedId, timetableResult, viewMode]);

  const activeClassId = viewMode === 'class'
    ? resolvedSelectedId
    : String(selectedCell ? (getCellForSlot(selectedCell.day, selectedCell.period)?.classId || classes[0]?.id || '') : classes[0]?.id || '');

  useEffect(() => {
    if (activeWindow === 'programEkrani') loadPendingLessons(activeClassId);
  }, [activeClassId, activeWindow, loadPendingLessons]);

  const refreshList = async (type) => {
    const endpoints = {
      subjects: ['dersler', setSubjects, normalizeSubjects],
      classes: ['siniflar', setClasses, normalizeClasses],
      teachers: ['ogretmenler', setTeachers, normalizeTeachers],
      assignments: ['atamalar', setAssignments, normalizeAssignments],
      classSubjects: ['sinif-dersleri', setClassSubjects, normalizeClassSubjects],
    };
    const config = endpoints[type];
    if (!config) return;
    const [endpoint, setter, normalizer] = config;
    const response = await axios.get(`${API}/${endpoint}`);
    setter(normalizer(getPayload(response)));
  };

  const updateTimetableCell = (classId, key, nextCell) => {
    setTimetableResult((prev) => {
      const next = JSON.parse(JSON.stringify(prev || { assignments: {} }));
      if (!next.assignments) next.assignments = {};
      if (!next.assignments[String(classId)]) next.assignments[String(classId)] = {};
      if (nextCell) next.assignments[String(classId)][key] = nextCell;
      else delete next.assignments[String(classId)][key];
      return next;
    });
  };

  const handleDrop = (day, period) => {
    if (!dragSource) return;
    const targetKey = slotKey(day, period);
    if (getCellForSlot(day, period)) {
      toast.error('Hedef hücre dolu');
      setDragSource(null);
      return;
    }
    updateTimetableCell(dragSource.classId, dragSource.key, null);
    updateTimetableCell(dragSource.classId, targetKey, dragSource.cell);
    setDragSource(null);
    setWarnings([{ message: 'Manuel taşıma yapıldı. Kaydet düğmesi timetableResult yapısını backend kaydına gönderir.' }]);
  };

  const handleCellDoubleClick = async (day, period) => {
    const key = slotKey(day, period);
    const cellData = getCellForSlot(day, period);
    const classId = cellData?.classId || (viewMode === 'class' ? resolvedSelectedId : activeClassId);
    if (!classId) return;
    try {
      const response = await axios.post(`${API}/hucre-bosa-al`, {
        classId,
        slotKey: key,
        timetableResult,
      });
      const payload = getPayload(response);
      setTimetableResult(extractTimetable(payload));
      setPendingLessons(extractPendingLessons(payload));
      setSelectedPendingLesson(null);
      setAvailabilityMap({});
      toast.success('Hücre boşa alındı');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Hücre boşa alınamadı');
    }
  };

  const handlePendingLessonClick = async (lesson) => {
    setSelectedPendingLesson(lesson);
    try {
      const response = await axios.post(`${API}/hucre-uygunluk`, {
        lesson,
        timetableResult,
        dailyConfigs: settings,
        classDailyLimits: dailyLimits,
        rules,
      });
      const payload = getPayload(response);
      setAvailabilityMap(normalizeAvailability(payload.availability || payload.availabilityList || payload.uygunluk || payload));
    } catch (error) {
      setAvailabilityMap({});
      toast.error(error.response?.data?.message || 'Hücre uygunluğu alınamadı');
    }
  };

  const handlePlacePendingLesson = async (day, period) => {
    if (!selectedPendingLesson) return;
    const key = slotKey(day, period);
    const availability = availabilityMap[key];
    if (availability && availability.status !== 'green') {
      toast.error(availability.message || 'Bu hücre uygun değil');
      return;
    }
    try {
      const response = await axios.post(`${API}/bekleyen-ders-yerlestir`, {
        lesson: selectedPendingLesson,
        slotKey: key,
        timetableResult,
        isFixed: true,
      });
      const payload = getPayload(response);
      setTimetableResult(extractTimetable(payload));
      const nextPending = extractPendingLessons(payload);
      if (nextPending.length > 0 || payload.pendingLessons || payload.bekleyenDersler) {
        setPendingLessons(nextPending);
      } else {
        await loadPendingLessons(valueOf(selectedPendingLesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], activeClassId));
      }
      setSelectedPendingLesson(null);
      setAvailabilityMap({});
      toast.success('Bekleyen ders yerleştirildi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bekleyen ders yerleştirilemedi');
    }
  };

  const handleAutoGenerate = async () => {
    try {
      const { data } = await axios.post(`${API}/otomatik`, {
        timetableResult,
        classDailyLimits: dailyLimits,
        dailyConfigs: settings,
        rules,
      });
      const payload = data?.data ?? data ?? {};
      setTimetableResult(normalizeTimetable(payload.timetableResult || payload));
      setWarnings(asArray(payload.warnings || payload.uyarilar));
      toast.success('Otomatik program oluşturuldu');
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Otomatik program oluşturulamadı';
      setWarnings([{ message }]);
      toast.error(message);
    }
  };

  const handleSaveProgram = async () => {
    try {
      const { data } = await axios.post(`${API}/kaydet`, {
        timetableResult,
        dailyConfigs: settings,
        classDailyLimits: dailyLimits,
        rules,
      });
      const payload = data?.data ?? data ?? {};
      setWarnings(asArray(payload.warnings || payload.uyarilar));
      toast.success('Program kaydedildi');
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Program kaydedilemedi';
      setWarnings([{ message }]);
      toast.error(message);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Tüm haftalık program temizlensin mi?')) return;
    try {
      await axios.delete(`${API}/temizle`);
      setTimetableResult({ assignments: {} });
      setSelectedCell(null);
      toast.success('Program temizlendi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Program temizlenemedi');
    }
  };

  const selectedCellData = selectedCell ? getCellForSlot(selectedCell.day, selectedCell.period) : null;
  const desktopModules = [
    { title: 'Dersler', icon: 'blocks', action: () => setActiveWindow('dersler') },
    { title: 'Sınıflar', icon: 'class', action: () => setActiveWindow('siniflar') },
    { title: 'Derslikler', icon: 'room', action: () => toast('Derslik yönetimi için backend endpointi tanımlı değil') },
    { title: 'Öğretmenler', icon: 'teachers', action: () => setActiveWindow('ogretmenler') },
    { title: 'Günlük\nDers Sayıları', icon: 'daily', action: () => setActiveWindow('gunlukDersSayilari') },
    { title: 'Sınıflarda\nOkutulan Dersler', icon: 'classSubjects', action: () => setActiveWindow('sinifDersleri') },
    { title: 'Öğretmenlere\nDers Atanması', icon: 'assignment', action: () => setActiveWindow('atamalar') },
    { title: 'Öğretmenlerin\nDers Programı', icon: 'teacherSchedule', action: () => { setViewMode('teacher'); setActiveWindow('programEkrani'); } },
    { title: 'Sınıfların\nDers Programı', icon: 'classSchedule', action: () => { setViewMode('class'); setActiveWindow('programEkrani'); } },
    { title: 'Dersliklerin\nDers Programı', icon: 'roomSchedule', action: () => toast('Derslik programı için backend endpointi tanımlı değil') },
    { title: 'Yeni Tablo\nOluşturulması', icon: 'newTable', action: handleAutoGenerate },
    { title: 'Öğretmenlerin\nNöbet Çizelgesi', icon: 'duty', action: () => toast('Nöbet çizelgesi için backend endpointi tanımlı değil') },
    { title: 'Ek Ders\nHesaplama', icon: 'extra', action: () => toast('Ek ders hesaplama bu modülün dışında') },
    { title: 'Bilgi Panosu', icon: 'board', action: () => toast('Bilgi panosu için backend endpointi tanımlı değil') },
    { title: 'Yedekleme', icon: 'backup', action: handleSaveProgram },
  ];

  return (
    <div className="hp-page pragmatic-page">
      <style>{styles}</style>
      <div className="prag-shell">
        <div className="prag-titlebar">
          <span className="prag-app-dot">A</span>
          <span>ASDER Sistem</span>
          <span className="prag-window-controls">- x</span>
        </div>
        <div className="prag-content">
          <aside className="prag-brand">
            <img className="prag-asder-logo" src={asderLogo} alt="ASDER Sistem" />
            <div className="prag-logo-text">ASDER Sistem</div>
            <div className="prag-dna">Akademik Yönetim Sistemi</div>
          </aside>

          <main className="prag-main">
            <div className="prag-top-row">
              <div className="prag-toolbar">
                <ToolbarTile icon={<FaUsers />} label="Programlar" onClick={() => setActiveWindow('programlar')} active />
                <ToolbarTile icon={<FaClone />} label="Program\nKopyala" onClick={() => toast('Program kopyalama için backend endpointi tanımlı değil')} />
                <ToolbarTile icon={<FaWrench />} label="Dosya\nBakımı" onClick={handleClear} />
                <ToolbarTile icon={<FaCog />} label="Ayarlar" onClick={() => setActiveWindow('ayarlar')} />
                <ToolbarTile icon={<FaExchangeAlt />} label="Çevir" onClick={() => setViewMode(viewMode === 'class' ? 'teacher' : viewMode === 'teacher' ? 'subject' : 'class')} muted />
                <ToolbarTile icon={<FaFileContract />} label="Satış-Sözleşme\nTalebi Oluştur" onClick={() => toast('Bu işlem ders programı modülüne bağlı değil')} />
                <ToolbarTile icon={<FaFileAlt />} label="Şifre Talebi\nOluştur" onClick={() => toast('Bu işlem ders programı modülüne bağlı değil')} muted />
              </div>
              <button className="prag-contact" type="button" onClick={() => toast('İletişim bilgileri kurum ayarlarından yönetilir')}>💬<span>İletişim</span></button>
            </div>

            <h1 className="prag-heading">HAFTALIK DERS PROGRAMI <span>(ASDER SİSTEM)</span></h1>
            <div className="prag-school">ASDER SİSTEM&nbsp;&nbsp; AKADEMİK YÖNETİM</div>

            {warnings.length > 0 && (
              <div className="hp-warning-strip pragmatic-warning">
                {warnings.slice(0, 3).map((item, index) => (
                  <span key={index}>{item.message || item.text || String(item)}</span>
                ))}
              </div>
            )}

            <div className="prag-module-grid">
              {desktopModules.map((item) => (
                <DesktopModule key={item.title} item={item} />
              ))}
            </div>
          </main>
        </div>
        <button className="prag-support" type="button">Destek Talebi Oluştur</button>
        <div className="prag-bottom-left">
          <button type="button">📌 Destek Talebi Oluştur</button>
        </div>
        <div className="prag-bottom-actions">
          <button type="button" onClick={handleSaveProgram}>💾 Net Yedekleme</button>
          <button type="button" onClick={() => toast('Çıkış için sol menüden oturumu kapatabilirsiniz')}>🚪 Programdan Çıkış</button>
        </div>
      </div>

      {activeWindow === 'programEkrani' && (
        <ScheduleProgramWindow
          viewMode={viewMode}
          setViewMode={setViewMode}
          visibleList={visibleList}
          resolvedSelectedId={resolvedSelectedId}
          setSelectedId={setSelectedId}
          activeDays={activeDays}
          periods={periods}
          settings={settings}
          setSettings={setSettings}
          subjectMap={subjectMap}
          classMap={classMap}
          teacherMap={teacherMap}
          timetableResult={timetableResult}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          selectedCell={selectedCell}
          selectedCellData={selectedCellData}
          setSelectedCell={setSelectedCell}
          setDragSource={setDragSource}
          getCellForSlot={getCellForSlot}
          handleDrop={handleDrop}
          handleCellDoubleClick={handleCellDoubleClick}
          handlePendingLessonClick={handlePendingLessonClick}
          handlePlacePendingLesson={handlePlacePendingLesson}
          updateTimetableCell={updateTimetableCell}
          pendingLessons={pendingLessons}
          assignments={assignments}
          classSubjects={classSubjects}
          setClassSubjects={setClassSubjects}
          selectedPendingLesson={selectedPendingLesson}
          setSelectedPendingLesson={setSelectedPendingLesson}
          availabilityMap={availabilityMap}
          setAvailabilityMap={setAvailabilityMap}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'dersler' && (
        <SubjectsWindow
          subjects={subjects}
          setSubjects={setSubjects}
          refresh={() => refreshList('subjects')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'siniflar' && (
        <ClassesWindow
          classes={classes}
          setClasses={setClasses}
          refresh={() => refreshList('classes')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'ayarlar' && (
        <SettingsWindow
          settings={settings}
          setSettings={setSettings}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'sinifDersleri' && (
        <ClassSubjectsWindow
          classes={classes}
          subjects={subjects}
          classSubjects={classSubjects}
          setClassSubjects={setClassSubjects}
          refresh={() => refreshList('classSubjects')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'gunlukDersSayilari' && (
        <DailyLimitsWindow
          classes={classes}
          settings={settings}
          dailyLimits={dailyLimits}
          setDailyLimits={setDailyLimits}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'ogretmenler' && (
        <TeachersWindow
          teachers={teachers}
          setTeachers={setTeachers}
          settings={settings}
          refresh={() => refreshList('teachers')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'atamalar' && (
        <AssignmentsWindow
          teachers={teachers}
          classes={classes}
          subjects={subjects}
          classSubjects={classSubjects}
          emptyClassSubjects={emptyClassSubjects}
          assignments={assignments}
          setAssignments={setAssignments}
          setEmptyClassSubjects={setEmptyClassSubjects}
          refresh={() => refreshList('assignments')}
          onClose={() => setActiveWindow(null)}
        />
      )}
      {activeWindow === 'programlar' && (
        <ProgramListWindow onClose={() => setActiveWindow(null)} setTimetableResult={setTimetableResult} />
      )}
    </div>
  );
}

function ToolbarTile({ icon, label, onClick, active = false, muted = false }) {
  return (
    <button className={`prag-toolbar-tile${active ? ' active' : ''}${muted ? ' muted' : ''}`} type="button" onClick={onClick}>
      <span>{icon}</span>
      <b>{label.split('\n').map((part, index) => <React.Fragment key={part}>{index > 0 && <br />}{part}</React.Fragment>)}</b>
    </button>
  );
}

function DesktopModule({ item }) {
  return (
    <button className="prag-module" type="button" onClick={item.action}>
      <span className={`prag-module-icon ${item.icon}`}>
        <DesktopIcon type={item.icon} />
      </span>
      <b>{item.title.split('\n').map((part, index) => <React.Fragment key={part}>{index > 0 && <br />}{part}</React.Fragment>)}</b>
    </button>
  );
}

function DesktopIcon({ type }) {
  const iconMap = {
    blocks: <FaBookOpen />,
    class: <FaSchool />,
    room: <FaBuilding />,
    teachers: <FaUserGraduate />,
    daily: <FaCalendarAlt />,
    classSubjects: <FaBook />,
    assignment: <FaTasks />,
    teacherSchedule: <FaChalkboardTeacher />,
    classSchedule: <FaTable />,
    roomSchedule: <FaUniversity />,
    newTable: <FaPlus />,
    duty: <FaBell />,
    extra: <FaMoneyBillWave />,
    board: <FaMapMarkedAlt />,
    backup: <FaArchive />,
  };
  return iconMap[type] || <FaTable />;
}

function ScheduleProgramWindow({
  viewMode,
  setViewMode,
  visibleList,
  resolvedSelectedId,
  setSelectedId,
  activeDays,
  periods,
  settings,
  setSettings,
  subjectMap,
  classMap,
  teacherMap,
  timetableResult,
  classes,
  subjects,
  teachers,
  selectedCell,
  selectedCellData,
  setSelectedCell,
  setDragSource,
  getCellForSlot,
  handleDrop,
  handleCellDoubleClick,
  handlePendingLessonClick,
  handlePlacePendingLesson,
  updateTimetableCell,
  pendingLessons,
  assignments = [],
  classSubjects = [],
  setClassSubjects,
  selectedPendingLesson,
  setSelectedPendingLesson,
  availabilityMap,
  setAvailabilityMap,
  onClose,
	}) {
	  const [lessonMenu, setLessonMenu] = useState(null);
	  const [placementEditor, setPlacementEditor] = useState(null);
	  const [autoSettingsOpen, setAutoSettingsOpen] = useState(false);
	  const [printOpen, setPrintOpen] = useState(false);
	  const [printOptions, setPrintOptions] = useState({ type: 'el', orientation: 'horizontal' });
	  const [autoSettings, setAutoSettings] = useState({
	    allowTeacherGaps: 'E',
	    maxWeeklyGaps: 2,
	    maxBetweenLessonGaps: 2,
	    sameClassLessonsDifferentDays: 'E',
	    splitTwoHourAsOnePlusOne: 'H',
	    maxDailyTeacherHours: 7,
	  });
	  const [localDays, setLocalDays] = useState(settings);
	  const nameHeader = viewMode === 'teacher' ? 'Adı Soyadı' : viewMode === 'subject' ? 'Dersin Adı' : 'Sınıfın Adı';
	  const detailHeader = viewMode === 'teacher' ? 'Branşı' : viewMode === 'subject' ? 'Kısa Adı' : 'Alan';
	  useEffect(() => {
	    setLocalDays(settings);
	  }, [settings]);
	  const classSubjectMap = useMemo(() => {
	    const map = {};
	    classSubjects.forEach((item) => {
	      map[`${String(item.classId)}_${String(item.subjectId)}`] = item;
	    });
	    return map;
	  }, [classSubjects]);
	  const classSubjectHours = useMemo(() => Object.fromEntries(
	    Object.entries(classSubjectMap).map(([key, item]) => [key, Number(item.weeklyHours || 0)])
	  ), [classSubjectMap]);
	  const getLessonHours = useCallback((lesson) => {
	    const subjectId = valueOf(lesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '');
	    const classId = valueOf(lesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], '');
	    return Number(valueOf(lesson, ['weeklyHours', 'weekly_hours', 'haftalikSaat', 'haftalik_saat', 'hds', 'hours'], 0)) ||
	      Number(classSubjectHours[`${String(classId)}_${String(subjectId)}`] || 0) ||
	      Number(subjectMap[String(subjectId)]?.weeklyHours || 0);
	  }, [classSubjectHours, subjectMap]);
	  const teacherTotals = useMemo(() => assignments.reduce((totals, row) => {
	    const teacherId = String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	    if (!teacherId) return totals;
	    totals[teacherId] = (totals[teacherId] || 0) + getLessonHours(row);
	    return totals;
	  }, {}), [assignments, getLessonHours]);
	  const visibleLessonRows = viewMode === 'teacher'
	    ? assignments.filter(row => String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')) === String(resolvedSelectedId))
	    : pendingLessons;
	  const selectedEntityTotal = viewMode === 'teacher'
	    ? Number(teacherTotals[String(resolvedSelectedId)] || 0)
	    : pendingLessons.reduce((total, lesson) => total + getLessonHours(lesson), 0);
	  const placedVisible = activeDays.reduce((total, day) => (
	    total + periods.filter(period => period < day.periodCount && getCellForSlot(day.dayIndex, period)).length
	  ), 0);
	  const totalVisibleSlots = activeDays.reduce((total, day) => total + Math.min(day.periodCount, periods.length), 0);
	  const openPlacementEditor = (lesson) => {
	    const subjectId = String(valueOf(lesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	    const classId = String(valueOf(lesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], resolvedSelectedId));
	    if (!classId || !subjectId) return toast.error('Ders ve sınıf bilgisi bulunamadı');
	    const classSubject = classSubjectMap[`${classId}_${subjectId}`] || {};
	    const subject = subjectMap[subjectId] || {};
	    const currentFormat = classSubject.distributionFormat || subject.distributionFormat || String(getLessonHours(lesson) || 1);
	    setPlacementEditor({ classId, subjectId, distributionFormat: currentFormat });
	    setLessonMenu(null);
	  };
	  const countPlacedForAssignment = (assignment) => {
	    const targetTeacherId = String(valueOf(assignment, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	    const targetClassId = String(valueOf(assignment, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
	    const targetSubjectId = String(valueOf(assignment, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	    return Object.entries(timetableResult?.assignments || {}).reduce((total, [placedClassId, slots]) => {
	      if (String(placedClassId) !== targetClassId) return total;
	      return total + Object.values(slots || {}).filter((cell) => {
	        const teacherIds = asArray(cell.teacherIds).map(String);
	        if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	        return String(cell.subjectId) === targetSubjectId && teacherIds.includes(targetTeacherId);
	      }).length;
	    }, 0);
	  };
	  const getDistributionParts = (assignment, remainingHours) => {
	    const subjectId = String(valueOf(assignment, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	    const classId = String(valueOf(assignment, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
	    const classSubject = classSubjectMap[`${classId}_${subjectId}`] || {};
	    const subject = subjectMap[subjectId] || {};
	    const rawFormat = classSubject.distributionFormat || subject.distributionFormat || String(remainingHours || 1);
	    const cleanFormat = String(rawFormat || '').replace(/\s+/g, '');
	    const parsed = /^\d+(\+\d+)*$/.test(cleanFormat) ? cleanFormat.split('+').map(Number).filter(Boolean) : [];
	    const sourceParts = parsed.length > 0 ? parsed : Array.from({ length: remainingHours }, () => 1);
	    const parts = [];
	    let left = remainingHours;
	    sourceParts.forEach((part) => {
	      if (left <= 0) return;
	      const next = Math.min(part, left);
	      if (next > 0) parts.push(next);
	      left -= next;
	    });
	    while (left > 0) {
	      parts.push(1);
	      left -= 1;
	    }
	    return parts;
	  };
	  const canPlaceBlock = (classId, teacherId, dayIndex, startPeriod, blockLength) => {
	    const day = activeDays.find(item => item.dayIndex === dayIndex);
	    if (!day || startPeriod + blockLength > day.periodCount) return false;
	    for (let offset = 0; offset < blockLength; offset += 1) {
	      const key = slotKey(dayIndex, startPeriod + offset);
	      if (timetableResult?.assignments?.[String(classId)]?.[key]) return false;
	      const teacherBusy = Object.entries(timetableResult?.assignments || {}).some(([, slots]) => {
	        const cell = slots?.[key];
	        if (!cell) return false;
	        const teacherIds = asArray(cell.teacherIds).map(String);
	        if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	        return teacherIds.includes(String(teacherId));
	      });
	      if (teacherBusy) return false;
	    }
	    return true;
	  };
	  const createAutomaticPlacements = (targetTeacherIds = null) => {
	    const targetSet = targetTeacherIds ? new Set(targetTeacherIds.map(String)) : null;
	    const blocked = new Set();
	    const teacherBusy = {};
	    const teacherDayHours = {};
	    Object.entries(timetableResult?.assignments || {}).forEach(([classId, slots]) => {
	      Object.entries(slots || {}).forEach(([key, cell]) => {
	        blocked.add(`${classId}_${key}`);
	        const dayIndex = Number(String(key).split('_')[0]);
	        const teacherIds = asArray(cell.teacherIds).map(String);
	        if (cell.teacherId) teacherIds.push(String(cell.teacherId));
	        teacherIds.forEach((teacherId) => {
	          if (!teacherBusy[teacherId]) teacherBusy[teacherId] = new Set();
	          if (!teacherDayHours[teacherId]) teacherDayHours[teacherId] = {};
	          teacherBusy[teacherId].add(key);
	          teacherDayHours[teacherId][dayIndex] = (teacherDayHours[teacherId][dayIndex] || 0) + 1;
	        });
	      });
	    });

	    const placements = [];
	    const orderedAssignments = [...assignments].sort((a, b) => {
	      const aTeacher = String(valueOf(a, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	      const bTeacher = String(valueOf(b, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	      if (aTeacher !== bTeacher) return aTeacher.localeCompare(bTeacher, 'tr');
	      return getLessonHours(b) - getLessonHours(a);
	    });

	    orderedAssignments.forEach((assignment) => {
	      const teacherId = String(valueOf(assignment, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], ''));
	      if (!teacherId || (targetSet && !targetSet.has(teacherId))) return;
	      const classId = String(valueOf(assignment, ['classId', 'class_id', 'sinifId', 'sinif_id'], ''));
	      const subjectId = String(valueOf(assignment, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	      const totalHours = getLessonHours(assignment);
	      const remainingHours = Math.max(0, totalHours - countPlacedForAssignment(assignment));
	      if (!classId || !subjectId || remainingHours <= 0) return;
	      const parts = getDistributionParts(assignment, remainingHours);
	      if (!teacherBusy[teacherId]) teacherBusy[teacherId] = new Set();
	      if (!teacherDayHours[teacherId]) teacherDayHours[teacherId] = {};

	      parts.forEach((blockLength) => {
	        let placed = false;
	        for (const day of activeDays) {
	          if (placed) break;
	          if ((teacherDayHours[teacherId][day.dayIndex] || 0) + blockLength > Number(autoSettings.maxDailyTeacherHours || 18)) continue;
	          for (const period of periods) {
	            if (period + blockLength > day.periodCount) continue;
	            let available = canPlaceBlock(classId, teacherId, day.dayIndex, period, blockLength);
	            for (let offset = 0; offset < blockLength; offset += 1) {
	              const key = slotKey(day.dayIndex, period + offset);
	              if (blocked.has(`${classId}_${key}`) || teacherBusy[teacherId].has(key)) available = false;
	            }
	            if (!available) continue;
	            for (let offset = 0; offset < blockLength; offset += 1) {
	              const key = slotKey(day.dayIndex, period + offset);
	              blocked.add(`${classId}_${key}`);
	              teacherBusy[teacherId].add(key);
	              teacherDayHours[teacherId][day.dayIndex] = (teacherDayHours[teacherId][day.dayIndex] || 0) + 1;
	              placements.push({ teacherId, classId, key, subjectId });
	            }
	            placed = true;
	            break;
	          }
	        }
	      });
	    });
	    return placements;
	  };
	  const applyPlacements = (placements, successMessage) => {
	    if (placements.length === 0) return toast.error('Yerleştirilecek uygun boş saat bulunamadı');
	    placements.forEach(({ teacherId, classId, key, subjectId }) => {
	      updateTimetableCell(classId, key, {
	        teacherId,
	        teacherIds: [teacherId],
	        subjectId,
	        isFixed: false,
	      });
	    });
	    setSelectedPendingLesson(null);
	    setAvailabilityMap({});
	    toast.success(successMessage || `${placements.length} saat ders yerleştirildi`);
	  };
	  const distributeSelectedTeacher = () => {
	    if (viewMode !== 'teacher') {
	      setViewMode('teacher');
	      return toast('Önce öğretmen seçin');
	    }
	    const teacherId = String(resolvedSelectedId || '');
	    if (!teacherId) return toast.error('Öğretmen seçin');
	    const teacherAssignments = assignments.filter(row => String(valueOf(row, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '')) === teacherId);
	    if (teacherAssignments.length === 0) return toast.error('Seçili öğretmene atanmış ders yok');

	    const placements = createAutomaticPlacements([teacherId]);
	    applyPlacements(placements, `${placements.length} saat ders yerleştirildi`);
	  };
	  const distributeAllProgram = () => {
	    if (assignments.length === 0) return toast.error('Önce öğretmenlere ders ataması yapılmalı');
	    const placements = createAutomaticPlacements();
	    applyPlacements(placements, `${placements.length} saatlik program oluşturuldu`);
	  };
	  const openAutoSettings = () => {
	    setLocalDays(settings);
	    setAutoSettingsOpen(true);
	  };
	  const updateLocalDay = (dayIndex, patch) => {
	    setLocalDays(prev => prev.map(day => (
	      Number(day.dayIndex) === Number(dayIndex)
	        ? { ...day, ...patch }
	        : day
	    )));
	  };
	  const saveAutoSettings = async () => {
	    const nextDays = localDays.map(day => ({
	      ...day,
	      periodCount: Math.max(1, Math.min(18, Number(day.periodCount || 1))),
	      isActive: Boolean(day.isActive),
	    }));
	    try {
	      await axios.put(`${API}/ayarlar`, {
	        dailyConfigs: nextDays,
	        daily_configs: nextDays,
	        autoSettings,
	        otomatikAyarlar: autoSettings,
	      });
	      setSettings(nextDays);
	      setAutoSettingsOpen(false);
	      toast.success('Ayarlar kaydedildi');
	    } catch (error) {
	      toast.error(error.response?.data?.message || 'Ayarlar kaydedilemedi');
	    }
	  };
	  const getPrintTitle = () => {
	    if (viewMode === 'teacher') return `${entityName(teacherMap, resolvedSelectedId)} Öğretmen Ders Programı`;
	    if (viewMode === 'subject') return `${entityName(subjectMap, resolvedSelectedId)} Ders Programı`;
	    return `${entityName(classMap, resolvedSelectedId)} Sınıf Ders Programı`;
	  };
	  const getPrintableRows = () => activeDays.map(day => ({
	    day,
	    cells: periods.map((period) => {
	      if (period >= day.periodCount) return null;
	      const lesson = getCellForSlot(day.dayIndex, period);
	      if (!lesson) return null;
	      const teacherId = lesson.cell.teacherId || lesson.cell.teacherIds?.[0];
	      const subjectId = lesson.cell.subjectId;
	      return {
	        className: entityName(classMap, lesson.classId, ''),
	        subjectName: entityName(subjectMap, subjectId, ''),
	        subjectShort: entityShort(subjectMap, subjectId, ''),
	        teacherName: entityName(teacherMap, teacherId, ''),
	      };
	    }),
	  }));
	  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
	    '&': '&amp;',
	    '<': '&lt;',
	    '>': '&gt;',
	    '"': '&quot;',
	    "'": '&#039;',
	  }[char]));
	  const buildPrintHtml = () => {
	    const rows = getPrintableRows();
	    const isVertical = printOptions.orientation === 'vertical';
	    const title = getPrintTitle();
	    const tableRows = rows.map(({ day, cells }) => `
	      <tr>
	        <th>${escapeHtml(day.name)}</th>
	        ${cells.map((cell) => `
	          <td>
	            ${cell ? `
	              <b>${escapeHtml(printOptions.type === 'el' ? cell.className : cell.subjectName)}</b>
	              <span>${escapeHtml(printOptions.type === 'el' ? cell.subjectShort : cell.teacherName)}</span>
	            ` : ''}
	          </td>
	        `).join('')}
	      </tr>
	    `).join('');
	    return `
	      <!doctype html>
	      <html>
	        <head>
	          <meta charset="utf-8" />
	          <title>${escapeHtml(title)}</title>
	          <style>
	            @page { size: ${isVertical ? 'A4 portrait' : 'A4 landscape'}; margin: 12mm; }
	            body { font-family: Tahoma, Arial, sans-serif; color:#111; }
	            h1 { font-size:18px; text-align:center; margin:0 0 12px; }
	            table { width:100%; border-collapse:collapse; table-layout:fixed; }
	            th, td { border:1px solid #333; padding:6px; height:${isVertical ? '58px' : '46px'}; vertical-align:top; font-size:12px; }
	            thead th { background:#b7d7f0; }
	            tbody th { background:#b7d7f0; width:95px; text-align:left; }
	            td b, td span { display:block; line-height:1.2; }
	            td b { font-weight:800; }
	          </style>
	        </head>
	        <body>
	          <h1>${escapeHtml(title)}</h1>
	          <table>
	            <thead>
	              <tr><th>Gün / Saat</th>${periods.map(period => `<th>${period + 1}</th>`).join('')}</tr>
	            </thead>
	            <tbody>${tableRows}</tbody>
	          </table>
	        </body>
	      </html>
	    `;
	  };
	  const openPrintPreview = () => {
	    const printWindow = window.open('', '_blank', 'width=1100,height=760');
	    if (!printWindow) return toast.error('Baskı önizleme penceresi açılamadı');
	    printWindow.document.write(buildPrintHtml());
	    printWindow.document.close();
	    printWindow.focus();
	    setTimeout(() => printWindow.print(), 250);
	  };
	  const exportPrintCsv = () => {
	    const rows = getPrintableRows();
	    const lines = [
	      ['Gün / Saat', ...periods.map(period => String(period + 1))],
	      ...rows.map(({ day, cells }) => [
	        day.name,
	        ...cells.map(cell => cell ? `${cell.className} ${cell.subjectShort} ${cell.teacherName}`.trim() : ''),
	      ]),
	    ];
	    const csv = lines.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(';')).join('\n');
	    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
	    const url = URL.createObjectURL(blob);
	    const link = document.createElement('a');
	    link.href = url;
	    link.download = `${getPrintTitle().replace(/[^\wğüşöçıİĞÜŞÖÇ-]+/gi, '_')}.csv`;
	    document.body.appendChild(link);
	    link.click();
	    document.body.removeChild(link);
	    URL.revokeObjectURL(url);
	  };
	  const savePlacementFormat = async () => {
	    if (!placementEditor) return;
	    const distributionFormat = String(placementEditor.distributionFormat || '').replace(/\s+/g, '');
	    const total = sumDistributionFormat(distributionFormat);
	    if (!total) return toast.error('Yerleşme biçimi 2+1 gibi olmalı');
	    const payload = {
	      classId: placementEditor.classId,
	      subjectId: placementEditor.subjectId,
	      sinifId: placementEditor.classId,
	      dersId: placementEditor.subjectId,
	      weeklyHours: total,
	      haftalikSaat: total,
	      distributionFormat,
	      distribution_format: distributionFormat,
	      placementFormat: distributionFormat,
	      placement_format: distributionFormat,
	      yerlesimBicimi: distributionFormat,
	      yerlesim_bicimi: distributionFormat,
	      yerlesmeBicimi: distributionFormat,
	      yerlesme_bicimi: distributionFormat,
	      yerBicimi: distributionFormat,
	      yer_bicimi: distributionFormat,
	    };
	    try {
	      await axios.put(`${API}/siniflar/${placementEditor.classId}/dersler/${placementEditor.subjectId}`, payload);
	      setClassSubjects(prev => prev.map(item => (
	        String(item.classId) === String(placementEditor.classId) && String(item.subjectId) === String(placementEditor.subjectId)
	          ? { ...item, weeklyHours: total, distributionFormat }
	          : item
	      )));
	      setPlacementEditor(null);
	      toast.success('Yerleşme biçimi güncellendi');
	    } catch (error) {
	      toast.error(error.response?.data?.message || 'Yerleşme biçimi kaydedilemedi');
	    }
	  };
	  return (
	    <WindowModal title="Ders Programları" width={1220} onClose={onClose}>
	      <div className="prag-program-tabs">
	        <button className={viewMode === 'teacher' ? 'active' : ''} onClick={() => setViewMode('teacher')}>Öğretmenlerin Ders Programı</button>
	        <button className={viewMode === 'class' ? 'active' : ''} onClick={() => setViewMode('class')}>Sınıfların Ders Programı</button>
	        <button className={viewMode === 'subject' ? 'active' : ''} onClick={() => setViewMode('subject')}>Derslerin Ders Programı</button>
	      </div>
	      <div className="prag-program-main full">
	        <div className="hp-grid-scroll">
	          <table className="hp-schedule-grid pragmatic-schedule-grid">
	            <thead>
	              <tr>
	                <th className="sticky-day">Gün / Saat</th>
	                {periods.map(period => <th key={period}>{period + 1}</th>)}
	              </tr>
	            </thead>
	            <tbody>
	              {activeDays.map((day) => (
	                <tr key={day.dayIndex}>
	                  <th className="sticky-day">{day.name}</th>
	                  {periods.map((period) => {
	                    const inactive = period >= day.periodCount;
	                    const lesson = inactive ? null : getCellForSlot(day.dayIndex, period);
	                    const subject = lesson ? subjectMap[String(lesson.cell.subjectId)] : null;
	                    const teacherId = lesson?.cell.teacherId || lesson?.cell.teacherIds?.[0];
	                    const key = slotKey(day.dayIndex, period);
	                    const availability = availabilityMap[key];
	                    const isDark = Boolean(lesson) && (period + day.dayIndex) % 3 !== 0;
	                    return (
	                      <td
	                        key={period}
	                        className={`${inactive ? 'inactive' : ''} ${lesson ? 'filled' : ''} ${isDark ? 'dark' : 'light'} ${availability ? `availability-${availability.status}` : ''}`}
	                        title={availability?.message || ''}
	                        onClick={() => !inactive && setSelectedCell({ day: day.dayIndex, period })}
	                        onDoubleClick={() => {
	                          if (inactive) return;
	                          if (selectedPendingLesson) handlePlacePendingLesson(day.dayIndex, period);
	                          else handleCellDoubleClick(day.dayIndex, period);
	                        }}
	                        onDragOver={(e) => !inactive && e.preventDefault()}
	                        onDrop={() => !inactive && handleDrop(day.dayIndex, period)}
	                      >
	                        {!inactive && lesson && (
	                          <div
	                            className="hp-lesson pragmatic-lesson"
	                            draggable={!lesson.cell.isFixed}
	                            onDragStart={() => setDragSource(lesson)}
	                          >
	                            <b>{subject?.shortName || lesson.cell.subjectName || lesson.cell.subjectId}</b>
	                            <span>{entityShort(teacherMap, teacherId)}</span>
	                            <small>{entityName(classMap, lesson.classId)}</small>
	                            {lesson.cell.isFixed && <FaLock className="lesson-lock" />}
	                          </div>
	                        )}
	                      </td>
	                    );
	                  })}
	                </tr>
	              ))}
	            </tbody>
	          </table>
	        </div>
	        <div className="prag-program-bottom wide">
	          <div className="prag-program-list">
	            <DenseTable headers={['No', nameHeader, detailHeader, 'T', 'K']} minWidth={420}>
	              {visibleList.map((item, index) => {
	                const isCurrent = String(item.id) === String(resolvedSelectedId);
	                const itemTotal = viewMode === 'teacher'
	                  ? Number(teacherTotals[String(item.id)] || 0)
	                  : isCurrent ? selectedEntityTotal : Number(item.weeklyHours || 0);
	                const itemPlaced = isCurrent ? placedVisible : 0;
	                return (
	                  <tr
	                    key={item.id}
	                    className={isCurrent ? 'selected' : ''}
	                    onClick={() => setSelectedId(String(item.id))}
	                  >
	                    <td>{index + 1}</td>
	                    <td>{item.name}</td>
	                    <td>{viewMode === 'teacher' ? item.branch : item.shortName || valueOf(item, ['area', 'alan'], '') || '-'}</td>
	                    <td>{itemTotal}</td>
	                    <td>{Math.max(0, itemTotal - itemPlaced)}</td>
	                  </tr>
	                );
	              })}
	            </DenseTable>
	          </div>
	          <DenseTable headers={['Sınıf', 'Dersin Adı', 'Kısa Adı', 'H', 'Derslik', 'K']} minWidth={620}>
	            {visibleLessonRows.map((lesson, index) => {
	              const subjectId = valueOf(lesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], '');
	              const teacherId = valueOf(lesson, ['teacherId', 'teacher_id', 'ogretmenId', 'ogretmen_id'], '');
	              const classId = valueOf(lesson, ['classId', 'class_id', 'sinifId', 'sinif_id'], resolvedSelectedId);
	              const lessonKey = valueOf(lesson, ['id', '_id'], `${classId}_${subjectId}_${teacherId}_${index}`);
	              const hours = getLessonHours(lesson);
	              const isSelected = selectedPendingLesson && String(valueOf(selectedPendingLesson, ['id', '_id'], '')) === String(valueOf(lesson, ['id', '_id'], '')) && String(subjectId) === String(valueOf(selectedPendingLesson, ['subjectId', 'subject_id', 'dersId', 'ders_id'], ''));
	              return (
	                <tr
	                  key={lessonKey}
	                  className={isSelected ? 'selected' : ''}
	                  onClick={() => handlePendingLessonClick(lesson)}
	                  onContextMenu={(event) => {
	                    event.preventDefault();
	                    setLessonMenu({ x: event.clientX, y: event.clientY, lesson });
	                  }}
	                >
	                  <td>{entityName(classMap, classId)}</td>
	                  <td>{entityName(subjectMap, subjectId, valueOf(lesson, ['subjectName', 'dersAdi', 'name'], '-'))}</td>
	                  <td>{entityShort(subjectMap, subjectId, '-')}</td>
	                  <td>{hours || ''}</td>
	                  <td />
	                  <td>{hours || ''}</td>
	                </tr>
	              );
	            })}
	            {visibleLessonRows.length === 0 && (
	              <tr><td colSpan={6}>{viewMode === 'teacher' ? 'Seçili öğretmene atanmış ders yok.' : 'Bekleyen ders yok. Hücreyi boşa almak için program hücresine çift tıklayın.'}</td></tr>
	            )}
	          </DenseTable>
	          <div className="prag-program-side">
	            <div className="prag-color-legend">
	              <div><b style={{ background:'#111827', color:'#fff' }}>1</b><span>Dr.Sa</span></div>
	              <div><b style={{ background:'#5ff06d' }}>2</b><span>Rh.Öğ</span></div>
	              <div><b style={{ background:'#ff57df' }}>3</b><span>Yön.</span></div>
	              <div><b style={{ background:'#b8ff6a' }}>4</b><span>Eg.</span></div>
	            </div>
	            <div className="prag-side-actions">
	              <button disabled>Geç</button>
	              <button disabled>Durdur</button>
	            </div>
	            <div className="prag-time-row"><span>00:00:00</span><span>0</span></div>
	            <div className="prag-counts">
	              <label>Toplam <strong>{totalVisibleSlots}</strong></label>
	              <label>Yerleşen <strong>{placedVisible}</strong></label>
	              <label>Kalan <strong>{Math.max(0, totalVisibleSlots - placedVisible)}</strong></label>
	            </div>
	          </div>
	        </div>
	        <div className="prag-selected-row">
	          <DenseTable headers={['Sınıf', 'Dersin Adı', 'H', 'Öğretmen', 'Derslik', 'K']} minWidth={660}>
	            <tr>
	              <td>{selectedCellData ? entityName(classMap, selectedCellData.classId) : ''}</td>
	              <td>{selectedCellData ? entityName(subjectMap, selectedCellData.cell.subjectId) : ''}</td>
	              <td>{selectedCellData ? subjectMap[String(selectedCellData.cell.subjectId)]?.weeklyHours || '' : ''}</td>
	              <td>{selectedCellData ? entityName(teacherMap, selectedCellData.cell.teacherId || selectedCellData.cell.teacherIds?.[0]) : ''}</td>
	              <td />
	              <td>{selectedCellData ? subjectMap[String(selectedCellData.cell.subjectId)]?.weeklyHours || '' : ''}</td>
	            </tr>
	          </DenseTable>
	          <strong>SEÇİLİ HÜCRE</strong>
	        </div>
	      </div>
	      <div className="prag-program-actions">
	        <button type="button" onClick={() => setPrintOpen(true)}>▦ Yazdır</button>
	        <button type="button" onClick={distributeSelectedTeacher}>🚶 Öğretmen</button>
	        <button type="button" onClick={distributeAllProgram}>▣ Otomat</button>
	        <button>📋 Rapor</button>
	        <button>▣ Özel Ayarlar</button>
	        <button type="button" onClick={openAutoSettings}>🔧 Ayarlar</button>
	        <button>⚑ Sil</button>
	        <button disabled>◉ Düzenle</button>
	        <button onClick={onClose}>▥ Kapat</button>
	      </div>
	      <div className="prag-program-shortcuts">
	        <span>F3=Kapalılık Haritası</span>
	        <span>F8=Kapat/Aç</span>
	        <span>F9=Ders Dışı Ver/Sil</span>
	      </div>
	      {lessonMenu && (
	        <div
	          className="prag-lesson-menu"
	          style={{ left: lessonMenu.x, top: lessonMenu.y }}
	          onMouseLeave={() => setLessonMenu(null)}
	        >
	          <button type="button" onClick={() => toast('Sınıfa bağlan özelliği için backend alanı bekleniyor')}>Sınıfa Bağlan</button>
	          <button type="button" onClick={() => toast('Dersliğe bağlan özelliği için backend alanı bekleniyor')}>Dersliğe Bağlan</button>
	          <button type="button" className="active" onClick={() => openPlacementEditor(lessonMenu.lesson)}>Yerleşme Biçimi</button>
	          <button type="button" onClick={() => toast('Özellikler penceresi ayrıca bağlanabilir')}>Özellikler</button>
	          <button type="button" onClick={() => toast('Derslik silme için önce program hücresinden derslik seçimi gerekir')}>Dersliği Sil</button>
	          <button type="button" onClick={() => handlePendingLessonClick(lessonMenu.lesson)}>Uygunları Göster</button>
	        </div>
	      )}
	      {printOpen && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-print-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Yazdır</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setPrintOpen(false)}>x</button>
	            </div>
	            <div className="hp-print-body">
	              <div className="hp-print-options">
	                <div className="hp-print-group">
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.type === 'el'}
	                      onChange={() => setPrintOptions({ ...printOptions, type: 'el' })}
	                    />
	                    <span>El Programı</span>
	                  </label>
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.type === 'carsaf'}
	                      onChange={() => setPrintOptions({ ...printOptions, type: 'carsaf' })}
	                    />
	                    <span>Çarşaf Liste</span>
	                  </label>
	                </div>
	                <div className="hp-print-group">
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.orientation === 'vertical'}
	                      onChange={() => setPrintOptions({ ...printOptions, orientation: 'vertical' })}
	                    />
	                    <span>Günler Dikey</span>
	                  </label>
	                  <label>
	                    <input
	                      type="radio"
	                      checked={printOptions.orientation === 'horizontal'}
	                      onChange={() => setPrintOptions({ ...printOptions, orientation: 'horizontal' })}
	                    />
	                    <span>Günler Yatay</span>
	                  </label>
	                </div>
	              </div>
	              <div className="hp-print-actions">
	                <button type="button" onClick={openPrintPreview}>▦ Baskı Ön İzleme</button>
	                <button type="button" onClick={exportPrintCsv}>▣ Excel'e Aktar</button>
	                <button type="button" onClick={openPrintPreview}>▦ Yazıcı Özellikleri</button>
	                <button type="button" onClick={() => setPrintOpen(false)}>▥ Kapat</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	      {placementEditor && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-placement-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Yerleşme Biçimi</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setPlacementEditor(null)}>x</button>
	            </div>
	            <div className="hp-form-card">
	              <Field label="Sınıf"><input value={entityName(classMap, placementEditor.classId)} readOnly /></Field>
	              <Field label="Ders"><input value={entityName(subjectMap, placementEditor.subjectId)} readOnly /></Field>
	              <Field label="Yerleşme biçimi">
	                <input
	                  placeholder="2+1"
	                  value={placementEditor.distributionFormat}
	                  onChange={event => setPlacementEditor({ ...placementEditor, distributionFormat: event.target.value })}
	                />
	              </Field>
	              <p className="hp-muted">Örnek: 3 saatlik ders için 2+1, 5 saatlik ders için 2+2+1.</p>
	              <div className="hp-row-actions">
	                <button type="button" onClick={savePlacementFormat}>Kaydet</button>
	                <button type="button" onClick={() => setPlacementEditor(null)}>İptal</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	      {autoSettingsOpen && (
	        <div className="hp-nested-modal">
	          <div className="hp-window small hp-auto-settings-window">
	            <div className="hp-window-titlebar">
	              <div><strong>Otomat Ayarları</strong></div>
	              <button className="hp-icon-btn" type="button" onClick={() => setAutoSettingsOpen(false)}>x</button>
	            </div>
	            <div className="hp-auto-settings-body">
	              <div className="hp-auto-settings-form">
	                <label>
	                  <span>1 - Öğretmen programlarında dersler arasında boşluk verilsin mi?</span>
	                  <select value={autoSettings.allowTeacherGaps} onChange={event => setAutoSettings({ ...autoSettings, allowTeacherGaps: event.target.value })}>
	                    <option value="E">E</option>
	                    <option value="H">H</option>
	                  </select>
	                </label>
	                <label>
	                  <span>2 - Bir haftada boşluk sayısı en fazla kaç olsun?</span>
	                  <input type="number" min="0" max="20" value={autoSettings.maxWeeklyGaps} onChange={event => setAutoSettings({ ...autoSettings, maxWeeklyGaps: Number(event.target.value) })} />
	                </label>
	                <label>
	                  <span>3 - İki ders arasında en fazla kaç boşluk olsun?</span>
	                  <input type="number" min="0" max="20" value={autoSettings.maxBetweenLessonGaps} onChange={event => setAutoSettings({ ...autoSettings, maxBetweenLessonGaps: Number(event.target.value) })} />
	                </label>
	                <label>
	                  <span>4 - Öğretmen aynı sınıfın birden fazla dersine giriyorsa farklı günlere gelsin mi?</span>
	                  <select value={autoSettings.sameClassLessonsDifferentDays} onChange={event => setAutoSettings({ ...autoSettings, sameClassLessonsDifferentDays: event.target.value })}>
	                    <option value="E">E</option>
	                    <option value="H">H</option>
	                  </select>
	                </label>
	                <label>
	                  <span>5 - Bir ders 2 biçiminde dağıtılıyorsa 1+1 biçiminde birleştirilmesine izin verilsin mi?</span>
	                  <select value={autoSettings.splitTwoHourAsOnePlusOne} onChange={event => setAutoSettings({ ...autoSettings, splitTwoHourAsOnePlusOne: event.target.value })}>
	                    <option value="H">H</option>
	                    <option value="E">E</option>
	                  </select>
	                </label>
	                <label>
	                  <span>6 - Bir öğretmene günde en fazla kaç saat ders verilsin?</span>
	                  <input type="number" min="1" max="18" value={autoSettings.maxDailyTeacherHours} onChange={event => setAutoSettings({ ...autoSettings, maxDailyTeacherHours: Number(event.target.value) })} />
	                </label>
	              </div>
	              <div className="hp-auto-settings-days">
	                <strong>Gün Kapat / Aç</strong>
	                {localDays.map(day => (
	                  <label key={day.dayIndex}>
	                    <input
	                      type="checkbox"
	                      checked={Boolean(day.isActive)}
	                      onChange={event => updateLocalDay(day.dayIndex, { isActive: event.target.checked })}
	                    />
	                    <span>{day.name}</span>
	                    <input
	                      type="number"
	                      min="1"
	                      max="18"
	                      value={day.periodCount}
	                      onChange={event => updateLocalDay(day.dayIndex, { periodCount: Number(event.target.value) })}
	                    />
	                  </label>
	                ))}
	              </div>
	              <div className="hp-auto-settings-actions">
	                <button type="button" onClick={saveAutoSettings}>✓ Tamam</button>
	                <button type="button" onClick={() => setAutoSettingsOpen(false)}>✕ İptal</button>
	                <button type="button" onClick={() => toast('Ayrıntılar ekranı ayrıca backend kurallarıyla bağlanabilir')}>Ayrıntılar</button>
	              </div>
	            </div>
	          </div>
	        </div>
	      )}
	    </WindowModal>
  );
}

function MiniList({ title, items, icon, onManage }) {
  return (
    <div className="hp-mini-panel">
      <div className="hp-panel-title">{icon} {title}<button type="button" onClick={onManage}>Yönet</button></div>
      <div className="hp-mini-list">
        {items.slice(0, 12).map(item => <span key={item.id}>{item.shortName || item.name}</span>)}
      </div>
    </div>
  );
}

function CellInspector({ selectedCell, cellData, subjects, teachers, classes, subjectMap, teacherMap, classMap, updateTimetableCell, setSelectedCell, viewClassId }) {
  const [form, setForm] = useState({ classId: '', subjectId: '', teacherId: '', isFixed: true });

  useEffect(() => {
    if (!selectedCell) return;
    setForm({
      classId: cellData?.classId || viewClassId || String(classes[0]?.id || ''),
      subjectId: String(cellData?.cell?.subjectId || subjects[0]?.id || ''),
      teacherId: String(cellData?.cell?.teacherId || cellData?.cell?.teacherIds?.[0] || teachers[0]?.id || ''),
      isFixed: Boolean(cellData?.cell?.isFixed ?? true),
    });
  }, [cellData, classes, selectedCell, subjects, teachers, viewClassId]);

  if (!selectedCell) {
    return (
      <div className="hp-mini-panel hp-cell-panel">
        <div className="hp-panel-title">Seçili Hücre</div>
        <p className="hp-muted">Düzenlemek için grid üzerinde bir hücre seçin.</p>
      </div>
    );
  }

  const save = () => {
    const key = slotKey(selectedCell.day, selectedCell.period);
    updateTimetableCell(form.classId, key, {
      subjectId: String(form.subjectId),
      teacherId: String(form.teacherId),
      teacherIds: [String(form.teacherId)],
      isFixed: form.isFixed,
    });
    toast.success('Hücre güncellendi');
  };

  const remove = () => {
    if (cellData) updateTimetableCell(cellData.classId, cellData.key, null);
    setSelectedCell(null);
  };

  return (
    <div className="hp-mini-panel hp-cell-panel">
      <div className="hp-panel-title">Seçili Hücre</div>
      <div className="hp-cell-summary">
        <strong>{DAY_NAMES[selectedCell.day]} / {selectedCell.period + 1}. Saat</strong>
        <span>{cellData ? `${entityName(subjectMap, cellData.cell.subjectId)} - ${entityShort(teacherMap, cellData.cell.teacherId || cellData.cell.teacherIds?.[0])}` : 'Boş'}</span>
        {cellData && <small>{entityName(classMap, cellData.classId)}</small>}
      </div>
      <div className="hp-inline-form">
        <select value={form.classId} onChange={e => setForm({ ...form, classId: e.target.value })}>
          {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={form.subjectId} onChange={e => setForm({ ...form, subjectId: e.target.value })}>
          {subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={form.teacherId} onChange={e => setForm({ ...form, teacherId: e.target.value })}>
          {teachers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <label className="hp-check"><input type="checkbox" checked={form.isFixed} onChange={e => setForm({ ...form, isFixed: e.target.checked })} /> Sabit</label>
      </div>
      <div className="hp-row-actions">
        <button type="button" onClick={save}>Yaz</button>
        <button type="button" className="danger" onClick={remove}>Sil</button>
      </div>
    </div>
  );
}

function SubjectsWindow({ subjects, setSubjects, refresh, onClose }) {
  const [form, setForm] = useState(emptyForm.subject);
  const [editingId, setEditingId] = useState(null);

  const selectSubject = (subject) => {
    setEditingId(subject.id);
    setForm({
      ...emptyForm.subject,
      ...subject,
      weeklyHours: subject.weeklyHours || '',
      distributionFormat: subject.distributionFormat || '',
    });
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Ders adı gerekli');
    const distributionFormat = form.distributionFormat || String(Number(form.weeklyHours) || 1);
    const distributionTotal = sumDistributionFormat(distributionFormat);
    const weeklyHours = distributionTotal || Number(form.weeklyHours) || 1;
    const payload = {
      name: form.name,
      ad: form.name,
      dersAdi: form.name,
      ders_adi: form.name,
      dersinAdi: form.name,
      dersin_adi: form.name,
      shortName: form.shortName,
      short_name: form.shortName,
      kisaAd: form.shortName,
      kisa_ad: form.shortName,
      kisaAdi: form.shortName,
      kisa_adi: form.shortName,
      weeklyHours,
      weekly_hours: weeklyHours,
      haftalikDersSaati: weeklyHours,
      haftalik_ders_saati: weeklyHours,
      haftalikSaat: weeklyHours,
      haftalik_saat: weeklyHours,
      HDS: weeklyHours,
      hds: weeklyHours,
      distributionFormat,
      distribution_format: distributionFormat,
      placementFormat: distributionFormat,
      placement_format: distributionFormat,
      yerlesimBicimi: distributionFormat,
      yerlesim_bicimi: distributionFormat,
      yerlesmeBicimi: distributionFormat,
      yerlesme_bicimi: distributionFormat,
      yerBicimi: distributionFormat,
      yer_bicimi: distributionFormat,
      yerBic: distributionFormat,
      yerlesim: distributionFormat,
      yer_bic: distributionFormat,
      color: form.color,
      renk: form.color,
    };
    if (editingId) await axios.put(`${API}/dersler/${editingId}`, payload);
    else await axios.post(`${API}/dersler`, payload);
    setForm(emptyForm.subject);
    setEditingId(null);
    await refresh();
    toast.success('Ders kaydedildi');
  };

  const remove = async (id) => {
    if (!window.confirm('Ders silinsin mi?')) return;
    await axios.delete(`${API}/dersler/${id}`);
    setSubjects(subjects.filter(item => String(item.id) !== String(id)));
    toast.success('Ders silindi');
  };

  return (
    <WindowModal title="Dersler" width={1080} onClose={onClose}>
      <div className="hp-window-grid two">
        <div className="hp-form-card">
          <Field label="Ders adı"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Kısa adı"><input value={form.shortName} onChange={e => setForm({ ...form, shortName: e.target.value })} /></Field>
          <Field label="Haftalık ders saati"><input type="number" min="1" value={form.weeklyHours} onChange={e => setForm({ ...form, weeklyHours: e.target.value })} /></Field>
          <Field label="Yerleşim biçimi"><input placeholder="2+2+1" value={form.distributionFormat} onChange={e => {
            const nextFormat = e.target.value;
            const total = sumDistributionFormat(nextFormat);
            setForm({ ...form, distributionFormat: nextFormat, ...(total ? { weeklyHours: total } : {}) });
          }} /></Field>
          <Field label="Renk"><input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} /></Field>
          <div className="hp-row-actions">
            <button onClick={submit}><FaRegSave /> Kaydet</button>
            <button onClick={() => { setEditingId(null); setForm(emptyForm.subject); }}>Yeni</button>
          </div>
        </div>
        <DenseTable headers={['No', 'Dersin Adı', 'Kısa Adı', 'HDS', 'Yerleşim Biçimi', 'İşlem']}>
          {subjects.map((subject, index) => (
            <tr key={subject.id}>
              <td>{index + 1}</td>
              <td>{subject.name}</td>
              <td>{subject.shortName}</td>
              <td>{subject.weeklyHours}</td>
              <td>{subject.distributionFormat}</td>
              <td>
                <button className="hp-link-btn" onClick={() => selectSubject(subject)}>Düzenle</button>
                <button className="hp-link-btn danger" onClick={() => remove(subject.id)}>Sil</button>
              </td>
            </tr>
          ))}
        </DenseTable>
      </div>
    </WindowModal>
  );
}

function ClassesWindow({ classes, setClasses, refresh, onClose }) {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);

  const submit = async () => {
    if (!name.trim()) return toast.error('Sınıf adı gerekli');
    if (editingId) await axios.put(`${API}/siniflar/${editingId}`, { name });
    else await axios.post(`${API}/siniflar`, { name });
    setName('');
    setEditingId(null);
    await refresh();
    toast.success('Sınıf kaydedildi');
  };

  const remove = async (id) => {
    if (!window.confirm('Sınıf silinsin mi?')) return;
    await axios.delete(`${API}/siniflar/${id}`);
    setClasses(classes.filter(item => String(item.id) !== String(id)));
  };

  return (
    <WindowModal title="Sınıflar" width={760} onClose={onClose}>
      <div className="hp-window-grid two narrow">
        <div className="hp-form-card">
          <Field label="Sınıf adı"><input value={name} onChange={e => setName(e.target.value)} /></Field>
          <div className="hp-row-actions">
            <button onClick={submit}><FaRegSave /> Kaydet</button>
            <button onClick={() => toast('Sıralama backend order alanı ile korunur')}>Yukarı</button>
            <button onClick={() => toast('Sıralama backend order alanı ile korunur')}>Aşağı</button>
          </div>
        </div>
        <DenseTable headers={['No', 'Sınıfın Adı', 'İşlem']} minWidth={420}>
          {classes.map((klass, index) => (
            <tr key={klass.id}>
              <td>{index + 1}</td>
              <td>{klass.name}</td>
              <td>
                <button className="hp-link-btn" onClick={() => { setEditingId(klass.id); setName(klass.name); }}>Düzenle</button>
                <button className="hp-link-btn danger" onClick={() => remove(klass.id)}>Sil</button>
              </td>
            </tr>
          ))}
        </DenseTable>
      </div>
    </WindowModal>
  );
}

function SettingsWindow({ settings, setSettings, onClose }) {
  const [local, setLocal] = useState(settings);
  const [activeDay, setActiveDay] = useState(0);
  const day = local[activeDay] || local[0];

  const updateDay = (patch) => {
    setLocal(prev => prev.map(item => item.dayIndex === activeDay ? { ...item, ...patch } : item));
  };

  const updateSlot = (index, patch) => {
    updateDay({ timeSlots: day.timeSlots.map((slot, i) => i === index ? { ...slot, ...patch } : slot) });
  };

  const save = async () => {
    await axios.put(`${API}/ayarlar`, { dailyConfigs: local, daily_configs: local });
    setSettings(local);
    toast.success('Günler ve saatler kaydedildi');
    onClose();
  };

  return (
    <WindowModal title="Günler ve Saatler" width={1040} onClose={onClose}>
      <div className="hp-window-grid settings">
        <div className="hp-list-box">
          {local.map(item => (
            <button key={item.dayIndex} className={item.dayIndex === activeDay ? 'active' : ''} onClick={() => setActiveDay(item.dayIndex)}>
              <strong>{item.name}</strong><span>{item.shortName}</span>
            </button>
          ))}
        </div>
        <div className="hp-form-card">
          <div className="hp-settings-head">
            <Field label="Gün adı"><input value={day.name} onChange={e => updateDay({ name: e.target.value })} /></Field>
            <Field label="Kısa adı"><input value={day.shortName} onChange={e => updateDay({ shortName: e.target.value })} /></Field>
            <Field label="Aktif slot"><input type="number" min="1" max="18" value={day.periodCount} onChange={e => updateDay({ periodCount: Number(e.target.value) })} /></Field>
            <label className="hp-check"><input type="checkbox" checked={day.isActive} onChange={e => updateDay({ isActive: e.target.checked })} /> Aktif</label>
          </div>
          <DenseTable headers={['Slot', 'Başlangıç', 'Bitiş']} minWidth={420}>
            {Array.from({ length: 18 }, (_, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td><input type="time" value={day.timeSlots[index]?.start || '00:00'} onChange={e => updateSlot(index, { start: e.target.value })} /></td>
                <td><input type="time" value={day.timeSlots[index]?.end || '00:00'} onChange={e => updateSlot(index, { end: e.target.value })} /></td>
              </tr>
            ))}
          </DenseTable>
          <div className="hp-row-actions"><button onClick={save}><FaRegSave /> Kaydet</button></div>
        </div>
      </div>
    </WindowModal>
  );
}

function ClassSubjectsWindow({ classes, subjects, classSubjects, setClassSubjects, refresh, onClose }) {
  const [classId, setClassId] = useState(String(classes[0]?.id || ''));
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargetClassId, setCopyTargetClassId] = useState('');
  const [commonOpen, setCommonOpen] = useState(false);
  const [commonSubjectId, setCommonSubjectId] = useState('');
  const [commonPickedClassIds, setCommonPickedClassIds] = useState([]);
  const [commonGroups, setCommonGroups] = useState([]);
  const [selectedCommonGroupId, setSelectedCommonGroupId] = useState('');
  const currentSubjectIds = new Set(classSubjects.filter(item => String(item.classId) === String(classId)).map(item => String(item.subjectId)));
  const selectedClassSubjectRows = classSubjects
    .filter(item => String(item.classId) === String(classId))
    .map(item => {
      const subject = subjects.find(subjectItem => String(subjectItem.id) === String(item.subjectId)) || {};
      return {
        ...item,
        subject,
        name: subject.name || '-',
        shortName: subject.shortName || '',
        weeklyHours: Number(item.weeklyHours || subject.weeklyHours || 0),
        distributionFormat: item.distributionFormat || subject.distributionFormat || '',
      };
    });
  const selectedSubjects = subjects.filter(item => currentSubjectIds.has(String(item.id)));
  const availableSubjects = subjects;
  const selectedClass = classes.find(item => String(item.id) === String(classId));
  const selectedSubject = subjects.find(item => String(item.id) === String(selectedSubjectId));
  const selectedWeeklyTotal = selectedClassSubjectRows.reduce((total, item) => total + Number(item.weeklyHours || 0), 0);

  useEffect(() => {
    if (!classId && classes[0]) setClassId(String(classes[0].id));
  }, [classId, classes]);

  const addSubject = async (subjectId) => {
    if (!classId) return toast.error('Önce sınıf seçin');
    if (!subjectId) return toast.error('Ders seçin');
    if (currentSubjectIds.has(String(subjectId))) return toast('Bu ders zaten sınıfa atanmış');

    const subject = subjects.find(item => String(item.id) === String(subjectId));
    await axios.post(`${API}/sinif-dersleri`, {
      classId,
      subjectId,
      sinifId: classId,
      dersId: subjectId,
      weeklyHours: subject?.weeklyHours || 0,
      haftalikSaat: subject?.weeklyHours || 0,
      distributionFormat: subject?.distributionFormat || '',
      yerlesimBicimi: subject?.distributionFormat || '',
    });
    setClassSubjects([
      ...classSubjects,
      {
        id: `local-${classId}-${subjectId}`,
        classId: String(classId),
        subjectId: String(subjectId),
        weeklyHours: Number(subject?.weeklyHours || 0),
        distributionFormat: subject?.distributionFormat || '',
      },
    ]);
    setSelectedSubjectId('');
    refresh();
    toast.success('Ders sınıfa atandı');
  };

  const removeSubject = async (subjectId) => {
    await axios.delete(`${API}/siniflar/${classId}/dersler/${subjectId}`);
    setClassSubjects(classSubjects.filter(item => !(String(item.classId) === String(classId) && String(item.subjectId) === String(subjectId))));
    toast.success('Ders kaldırıldı');
  };

  const saveAll = async () => {
    const subjectIds = Array.from(currentSubjectIds);
    await axios.put(`${API}/siniflar/${classId}/dersler`, { subjectIds, dersler: subjectIds });
    toast.success('Sınıf dersleri kaydedildi');
  };

  const openCopy = () => {
    const firstTarget = classes.find(item => String(item.id) !== String(classId));
    setCopyTargetClassId(String(firstTarget?.id || ''));
    setCopyOpen(true);
  };

  const copyLessons = async () => {
    if (!classId) return toast.error('Kaynak sınıf seçin');
    if (!copyTargetClassId) return toast.error('Kopyalanacak sınıf seçin');
    if (String(classId) === String(copyTargetClassId)) return toast.error('Aynı sınıfa kopyalama yapılamaz');

    const subjectIds = Array.from(currentSubjectIds);
    await axios.put(`${API}/siniflar/${copyTargetClassId}/dersler`, { subjectIds, dersler: subjectIds });
    const nextRows = classSubjects.filter(item => String(item.classId) !== String(copyTargetClassId));
    const copiedRows = selectedClassSubjectRows.map(row => ({
      id: `local-${copyTargetClassId}-${row.subjectId}`,
      classId: String(copyTargetClassId),
      subjectId: String(row.subjectId),
      weeklyHours: Number(row.weeklyHours || 0),
      distributionFormat: row.distributionFormat || '',
    }));
    setClassSubjects([...nextRows, ...copiedRows]);
    setCopyOpen(false);
    toast.success('Dersler kopyalandı');
    refresh();
  };

  const openCommonLessons = () => {
    const initialSubjectId = selectedSubjectId || subjects[0]?.id || '';
    setCommonSubjectId(String(initialSubjectId));
    setCommonPickedClassIds([]);
    setSelectedCommonGroupId('');
    setCommonOpen(true);
  };

  const subjectClassRows = classSubjects
    .filter(item => String(item.subjectId) === String(commonSubjectId))
    .map(item => ({
      ...item,
      className: classes.find(klass => String(klass.id) === String(item.classId))?.name || '-',
      subjectName: subjects.find(subject => String(subject.id) === String(item.subjectId))?.name || '-',
      weeklyHours: item.weeklyHours || subjects.find(subject => String(subject.id) === String(item.subjectId))?.weeklyHours || 0,
    }));
  const pickedRows = subjectClassRows.filter(item => commonPickedClassIds.includes(String(item.classId)));
  const activeCommonGroups = commonGroups.filter(group => String(group.subjectId) === String(commonSubjectId));
  const groupedRows = activeCommonGroups.flatMap(group => group.classIds.map(groupClassId => {
    const row = subjectClassRows.find(item => String(item.classId) === String(groupClassId));
    return row ? { ...row, groupId: group.id } : null;
  }).filter(Boolean));

  const toggleCommonClass = (targetClassId) => {
    setCommonPickedClassIds(prev => (
      prev.includes(String(targetClassId))
        ? prev.filter(id => id !== String(targetClassId))
        : [...prev, String(targetClassId)]
    ));
  };

  const combineCommonLessons = () => {
    if (!commonSubjectId) return toast.error('Ders seçin');
    if (commonPickedClassIds.length < 2) return toast.error('En az iki sınıf seçin');
    const groupId = `common-${commonSubjectId}-${Date.now()}`;
    const nextGroups = [
      ...commonGroups.filter(group => !(String(group.subjectId) === String(commonSubjectId) && group.classIds.some(id => commonPickedClassIds.includes(String(id))))),
      { id: groupId, subjectId: String(commonSubjectId), classIds: commonPickedClassIds },
    ];
    setCommonGroups(nextGroups);
    setClassSubjects(classSubjects.map(item => (
      String(item.subjectId) === String(commonSubjectId) && commonPickedClassIds.includes(String(item.classId))
        ? { ...item, commonGroupId: groupId }
        : item
    )));
    setSelectedCommonGroupId(groupId);
    toast.success('Ortak ders grubu oluşturuldu');
  };

  const dissolveCommonLesson = () => {
    if (!selectedCommonGroupId) return toast.error('Çözülecek ortak grubu seçin');
    setCommonGroups(commonGroups.filter(group => String(group.id) !== String(selectedCommonGroupId)));
    setClassSubjects(classSubjects.map(item => (
      String(item.commonGroupId) === String(selectedCommonGroupId)
        ? { ...item, commonGroupId: '' }
        : item
    )));
    setSelectedCommonGroupId('');
    toast.success('Ortak ders grubu çözüldü');
  };

  return (
    <WindowModal title="Sınıflarda Okutulan Dersler" width={1280} onClose={onClose}>
      <div className="hp-class-subjects">
        <div className="hp-class-col">
          <div className="hp-panel-title">Sınıflar</div>
          <DenseTable headers={['Sınıfın Adı', 'Alan', 'Top.']} minWidth={250}>
            {classes.map(item => {
              const itemSubjectIds = new Set(classSubjects.filter(row => String(row.classId) === String(item.id)).map(row => String(row.subjectId)));
              const total = subjects
                .filter(subject => itemSubjectIds.has(String(subject.id)))
                .reduce((sum, subject) => sum + Number(subject.weeklyHours || 0), 0);
              const isActive = String(item.id) === String(classId);
              return (
                <tr
                  key={item.id}
                  className={isActive ? 'selected' : ''}
                  onClick={() => {
                    setClassId(String(item.id));
                    setSelectedSubjectId('');
                  }}
                >
                  <td>{item.name}</td>
                  <td>{valueOf(item, ['area', 'alan'], '') || '-'}</td>
                  <td>{total}</td>
                </tr>
              );
            })}
          </DenseTable>
        </div>

        <div className="hp-assigned-col">
          <div className="hp-panel-title">Sınıfta Okutulan Dersler</div>
          <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS', 'Yer.Biç.', '']} minWidth={560}>
            {selectedClassSubjectRows.length === 0 ? (
              <tr><td colSpan={5} className="hp-empty-cell">{selectedClass?.name || '-'} için ders atanmadı</td></tr>
            ) : selectedClassSubjectRows.map(item => (
              <tr key={item.id || `${item.classId}-${item.subjectId}`}>
                <td>{selectedClass?.name || '-'}</td>
                <td>{item.name}</td>
                <td>{item.weeklyHours || ''}</td>
                <td>{item.distributionFormat || ''}</td>
                <td><button className="hp-link-btn danger" onClick={() => removeSubject(item.subjectId)}>Sil</button></td>
              </tr>
            ))}
          </DenseTable>
        </div>

        <div className="hp-subject-col">
          <div className="hp-panel-title">Dersler</div>
          <DenseTable headers={['Dersin Adı', 'HDS', '']} minWidth={360}>
            {availableSubjects.length === 0 ? (
              <tr><td colSpan={3} className="hp-empty-cell">Atanacak ders kalmadı</td></tr>
            ) : availableSubjects.map(item => (
              <tr
                key={item.id}
                className={String(item.id) === String(selectedSubjectId) ? 'selected' : ''}
                onClick={() => setSelectedSubjectId(String(item.id))}
                onDoubleClick={() => addSubject(item.id)}
              >
                <td>{item.name}</td>
                <td>{item.weeklyHours || ''}</td>
                <td><button className="hp-link-btn" onClick={(event) => { event.stopPropagation(); addSubject(item.id); }}>Dersi Ata</button></td>
              </tr>
            ))}
          </DenseTable>
        </div>

        <div className="hp-class-subject-actions">
          <button className="hp-wide-btn" onClick={openCopy}>Ders Kopyala</button>
          <button className="hp-wide-btn" onClick={() => addSubject(selectedSubjectId)} disabled={!selectedSubjectId}>Dersi Ata</button>
          <button className="hp-wide-btn" onClick={saveAll}>Kaydet</button>
          <button className="hp-wide-btn" onClick={openCommonLessons}>Ortak Ders Tanımla</button>
          <button className="hp-wide-btn" onClick={onClose}>Kapat</button>
        </div>

        <div className="hp-class-subject-status">
          <span>F4 = Ders Kopyala</span>
          <span>F6 = Ortak Dersler Tanımla</span>
          <span>F7 = Sil</span>
          <span>Enter / Çift tık = Ders Ver</span>
          <strong>{selectedClass?.name || '-'} Toplam HDS: {selectedWeeklyTotal}</strong>
          {selectedSubject && <span>Seçili ders: {selectedSubject.name}</span>}
        </div>

        {copyOpen && (
          <div className="hp-copy-dialog">
            <div className="hp-copy-window">
              <div className="hp-window-titlebar">
                <div><strong>Ders Kopyala</strong></div>
                <button className="hp-icon-btn" type="button" onClick={() => setCopyOpen(false)}>x</button>
              </div>
              <div className="hp-copy-body">
                <div className="hp-copy-list">
                  <DenseTable headers={['No', 'Sınıfın Adı']} minWidth={220}>
                    {classes.filter(item => String(item.id) !== String(classId)).map((item, index) => (
                      <tr
                        key={item.id}
                        className={String(item.id) === String(copyTargetClassId) ? 'selected' : ''}
                        onClick={() => setCopyTargetClassId(String(item.id))}
                        onDoubleClick={copyLessons}
                      >
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                      </tr>
                    ))}
                  </DenseTable>
                </div>
                <div className="hp-copy-summary">
                  <div><span>Bu sınıftaki dersler</span><strong>{selectedClass?.name || '-'}</strong></div>
                  <div><span>Bu sınıfa kopyalanacak</span><strong>{classes.find(item => String(item.id) === String(copyTargetClassId))?.name || '-'}</strong></div>
                  <small>{selectedSubjects.length} ders, toplam {selectedWeeklyTotal} HDS</small>
                  <button className="hp-wide-btn" onClick={copyLessons}>Tamam</button>
                  <button className="hp-wide-btn" onClick={() => setCopyOpen(false)}>İptal</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {commonOpen && (
          <div className="hp-common-dialog">
            <div className="hp-common-window">
              <div className="hp-window-titlebar">
                <div><strong>Ortak Ders Tanımlama</strong></div>
                <button className="hp-icon-btn" type="button" onClick={() => setCommonOpen(false)}>x</button>
              </div>
              <div className="hp-common-body">
                <div className="hp-common-left">
                  <DenseTable headers={['No', 'Dersin Adı', 'HDS']} minWidth={320}>
                    {subjects.map((subject, index) => (
                      <tr
                        key={subject.id}
                        className={String(subject.id) === String(commonSubjectId) ? 'selected' : ''}
                        onClick={() => {
                          setCommonSubjectId(String(subject.id));
                          setCommonPickedClassIds([]);
                          setSelectedCommonGroupId('');
                        }}
                      >
                        <td>{index + 1}</td>
                        <td>{subject.name}</td>
                        <td>{subject.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>

                  <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={320}>
                    {subjectClassRows.length === 0 ? (
                      <tr><td colSpan={3} className="hp-empty-cell">Bu dersi alan sınıf yok</td></tr>
                    ) : subjectClassRows.map(row => (
                      <tr
                        key={`${row.classId}-${row.subjectId}`}
                        className={commonPickedClassIds.includes(String(row.classId)) ? 'selected' : ''}
                        onClick={() => toggleCommonClass(row.classId)}
                        onDoubleClick={combineCommonLessons}
                      >
                        <td>{row.className}</td>
                        <td>{row.subjectName}</td>
                        <td>{row.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>
                </div>

                <div className="hp-common-middle">
                  <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={300}>
                    {pickedRows.length === 0 ? (
                      <tr><td colSpan={3} className="hp-empty-cell">Birleştirilecek sınıfları seçin</td></tr>
                    ) : pickedRows.map(row => (
                      <tr key={`${row.classId}-${row.subjectId}`}>
                        <td>{row.className}</td>
                        <td>{row.subjectName}</td>
                        <td>{row.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>
                  <button className="hp-wide-btn" onClick={combineCommonLessons}>Birleştir</button>
                </div>

                <div className="hp-common-right">
                  <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={320}>
                    {groupedRows.length === 0 ? (
                      <tr><td colSpan={3} className="hp-empty-cell">Ortak ders grubu yok</td></tr>
                    ) : groupedRows.map(row => (
                      <tr
                        key={`${row.groupId}-${row.classId}`}
                        className={String(row.groupId) === String(selectedCommonGroupId) ? 'selected' : ''}
                        onClick={() => setSelectedCommonGroupId(String(row.groupId))}
                      >
                        <td>{row.className}</td>
                        <td>{row.subjectName}</td>
                        <td>{row.weeklyHours || ''}</td>
                      </tr>
                    ))}
                  </DenseTable>
                  <div className="hp-common-actions">
                    <button className="hp-wide-btn" onClick={dissolveCommonLesson}>Çöz</button>
                    <button className="hp-wide-btn" onClick={() => setCommonOpen(false)}>Kapat</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </WindowModal>
  );
}

function DailyLimitsWindow({ classes, settings, dailyLimits, setDailyLimits, onClose }) {
  const [classId, setClassId] = useState(String(classes[0]?.id || ''));
  const [local, setLocal] = useState({});

  useEffect(() => {
    const row = dailyLimits.find(item => String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) === String(classId)) || {};
    const limits = row.limits || row.gunler || {};
    const next = {};
    settings.forEach(day => {
      const current = limits[day.dayIndex] || limits[day.name] || {};
      next[day.dayIndex] = {
        start: Number(valueOf(current, ['start', 'baslangic', 'baslangicSlot'], 1)),
        end: Number(valueOf(current, ['end', 'bitis', 'bitisSlot'], day.periodCount)),
      };
    });
    setLocal(next);
  }, [classId, dailyLimits, settings]);

  const save = async () => {
    const payload = { classId, sinifId: classId, limits: local, classDailyLimits: local };
    await axios.put(`${API}/siniflar/${classId}/gunluk-ders-sayilari`, payload);
    const next = dailyLimits.filter(item => String(valueOf(item, ['classId', 'class_id', 'sinifId', 'sinif_id'], '')) !== String(classId));
    setDailyLimits([...next, payload]);
    toast.success('Günlük ders sayıları kaydedildi');
  };

  return (
    <WindowModal title="Günlük Ders Sayıları" width={940} onClose={onClose}>
      <div className="hp-window-grid settings">
        <div className="hp-list-box">
          {classes.map(item => <button key={item.id} className={String(item.id) === String(classId) ? 'active' : ''} onClick={() => setClassId(String(item.id))}>{item.name}</button>)}
        </div>
        <div className="hp-form-card">
          <DenseTable headers={['Gün', 'Başlangıç Slotu', 'Bitiş Slotu']} minWidth={520}>
            {settings.map(day => (
              <tr key={day.dayIndex}>
                <td>{day.name}</td>
                <td><input type="number" min="1" max="18" value={local[day.dayIndex]?.start || 1} onChange={e => setLocal({ ...local, [day.dayIndex]: { ...local[day.dayIndex], start: Number(e.target.value) } })} /></td>
                <td><input type="number" min="1" max="18" value={local[day.dayIndex]?.end || day.periodCount} onChange={e => setLocal({ ...local, [day.dayIndex]: { ...local[day.dayIndex], end: Number(e.target.value) } })} /></td>
              </tr>
            ))}
          </DenseTable>
          <div className="hp-row-actions"><button onClick={save}><FaRegSave /> Kaydet</button></div>
        </div>
      </div>
    </WindowModal>
  );
}

function TeachersWindow({ teachers, setTeachers, settings, refresh, onClose }) {
  const [form, setForm] = useState(emptyForm.teacher);
  const [editingId, setEditingId] = useState(null);
  const [unavailableTeacher, setUnavailableTeacher] = useState(null);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Öğretmen adı gerekli');
    const payload = {
      name: form.name,
      shortName: form.shortName,
      branch: form.branch,
      homeroomClass: form.homeroomClass,
      club: form.club,
      email: form.email,
    };
    if (editingId) await axios.put(`${API}/ogretmenler/${editingId}`, payload);
    else await axios.post(`${API}/ogretmenler`, payload);
    setForm(emptyForm.teacher);
    setEditingId(null);
    await refresh();
    toast.success('Öğretmen kaydedildi');
  };

  const remove = async (id) => {
    if (!window.confirm('Öğretmen silinsin mi?')) return;
    await axios.delete(`${API}/ogretmenler/${id}`);
    setTeachers(teachers.filter(item => String(item.id) !== String(id)));
  };

  return (
    <WindowModal title="Öğretmenler" width={1180} onClose={onClose}>
      <div className="hp-window-grid two">
        <div className="hp-form-card">
          <Field label="Öğretmen adı"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Kısa adı"><input value={form.shortName} onChange={e => setForm({ ...form, shortName: e.target.value })} /></Field>
          <Field label="Branş"><input value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} /></Field>
          <Field label="Sınıf öğretmeni"><input value={form.homeroomClass} onChange={e => setForm({ ...form, homeroomClass: e.target.value })} /></Field>
          <Field label="Sosyal kulübü"><input value={form.club} onChange={e => setForm({ ...form, club: e.target.value })} /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
          <div className="hp-row-actions"><button onClick={submit}><FaRegSave /> Kaydet</button><button onClick={() => { setEditingId(null); setForm(emptyForm.teacher); }}>Yeni</button></div>
        </div>
        <DenseTable headers={['No', 'Branşı', 'Adı Soyadı', 'Sınıf Öğr.', 'Sosyal Kulübü', 'İşlem']} minWidth={760}>
          {teachers.map((teacher, index) => (
            <tr key={teacher.id}>
              <td>{index + 1}</td>
              <td>{teacher.branch}</td>
              <td>{teacher.name}</td>
              <td>{teacher.homeroomClass}</td>
              <td>{teacher.club}</td>
              <td>
                <button className="hp-link-btn" onClick={() => { setEditingId(teacher.id); setForm(teacher); }}>Düzenle</button>
                <button className="hp-link-btn" onClick={() => setUnavailableTeacher(teacher)}>Kapalı Saat</button>
                <button className="hp-link-btn danger" onClick={() => remove(teacher.id)}>Sil</button>
              </td>
            </tr>
          ))}
        </DenseTable>
      </div>
      {unavailableTeacher && (
        <UnavailableGrid
          teacher={unavailableTeacher}
          settings={settings}
          onClose={() => setUnavailableTeacher(null)}
          onSaved={async () => { setUnavailableTeacher(null); await refresh(); }}
        />
      )}
    </WindowModal>
  );
}

function UnavailableGrid({ teacher, settings, onClose, onSaved }) {
  const [slots, setSlots] = useState(new Set(asArray(teacher.unavailableSlots)));
  const maxPeriod = Math.max(1, ...settings.filter(day => day.isActive).map(day => day.periodCount));

  const toggle = (key) => {
    const next = new Set(slots);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSlots(next);
  };

  const save = async () => {
    await axios.put(`${API}/ogretmenler/${teacher.id}/unavailable`, { unavailableSlots: Array.from(slots), kapaliSaatler: Array.from(slots) });
    toast.success('Kapalı saatler kaydedildi');
    onSaved();
  };

  return (
    <div className="hp-nested-modal">
      <div className="hp-window small">
        <div className="hp-window-titlebar"><strong>{teacher.name} Kapalı Saatleri</strong><button onClick={onClose}>x</button></div>
        <div className="hp-grid-scroll">
          <table className="hp-table hp-unavailable">
            <thead><tr><th>Gün</th>{Array.from({ length: maxPeriod }, (_, i) => <th key={i}>{i + 1}</th>)}</tr></thead>
            <tbody>
              {settings.filter(day => day.isActive).map(day => (
                <tr key={day.dayIndex}>
                  <td>{day.name}</td>
                  {Array.from({ length: maxPeriod }, (_, period) => {
                    const key = slotKey(day.dayIndex, period);
                    return <td key={period} className={period >= day.periodCount ? 'inactive' : ''}><button className={slots.has(key) ? 'closed' : ''} disabled={period >= day.periodCount} onClick={() => toggle(key)}>{slots.has(key) ? 'Kapalı' : 'Açık'}</button></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="hp-row-actions"><button onClick={save}>Kaydet</button></div>
      </div>
    </div>
  );
}

function AssignmentsWindow({ teachers, classes, subjects, classSubjects, emptyClassSubjects, assignments, setAssignments, setEmptyClassSubjects, refresh, onClose }) {
  const [teacherId, setTeacherId] = useState(String(teachers[0]?.id || ''));
  const [subjectId, setSubjectId] = useState(String(subjects[0]?.id || ''));
  const [classId, setClassId] = useState(String(classes[0]?.id || ''));
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [selectedEmptyKey, setSelectedEmptyKey] = useState('');
  const teacherMap = useMemo(() => Object.fromEntries(teachers.map(item => [String(item.id), item])), [teachers]);
  const classMap = useMemo(() => Object.fromEntries(classes.map(item => [String(item.id), item])), [classes]);
  const subjectMap = useMemo(() => Object.fromEntries(subjects.map(item => [String(item.id), item])), [subjects]);

  useEffect(() => {
    if (!teacherId && teachers[0]) setTeacherId(String(teachers[0].id));
    if (!subjectId && subjects[0]) setSubjectId(String(subjects[0].id));
    if (!classId && classes[0]) setClassId(String(classes[0].id));
  }, [classId, classes, subjectId, subjects, teacherId, teachers]);

  const currentRows = assignments.filter(item => String(item.teacherId) === String(teacherId));
  const selectedTeacher = teacherMap[String(teacherId)];
  const selectedSubject = subjectMap[String(subjectId)];
  const subjectClassIds = classSubjects
    .filter(item => String(item.subjectId) === String(subjectId))
    .map(item => String(item.classId));
  const classRows = (subjectClassIds.length > 0 ? subjectClassIds.map(id => classMap[id]).filter(Boolean) : classes);
  const teacherWeeklyTotal = currentRows.reduce((total, item) => total + Number(subjectMap[String(item.subjectId)]?.weeklyHours || item.weeklyHours || 0), 0);
  const fallbackEmptyLessons = classSubjects.filter(item => !assignments.some(row => (
    String(row.classId) === String(item.classId) &&
    String(row.subjectId) === String(item.subjectId)
  )));
  const emptyLessonSource = Array.isArray(emptyClassSubjects) ? emptyClassSubjects : fallbackEmptyLessons;
  const emptyLessonRows = emptyLessonSource
    .map(item => ({
      ...item,
      key: `${item.classId}-${item.subjectId}`,
      className: entityName(classMap, item.classId),
      subjectName: entityName(subjectMap, item.subjectId),
      weeklyHours: subjectMap[String(item.subjectId)]?.weeklyHours || item.weeklyHours || 0,
    }));
  const selectedEmptyLesson = emptyLessonRows.find(item => item.key === selectedEmptyKey) || emptyLessonRows[0];

  const add = async (targetClassId = classId, targetSubjectId = subjectId) => {
    if (!teacherId) return toast.error('Öğretmen seçin');
    if (!targetSubjectId) return toast.error('Ders seçin');
    if (!targetClassId) return toast.error('Sınıf seçin');
    const targetSubject = subjectMap[String(targetSubjectId)];
    const exists = assignments.some(item => (
      String(item.teacherId) === String(teacherId) &&
      String(item.subjectId) === String(targetSubjectId) &&
      String(item.classId) === String(targetClassId)
    ));
    if (exists) return toast('Bu atama zaten var');

    await axios.post(`${API}/atamalar`, {
      teacherId,
      subjectId: targetSubjectId,
      classId: targetClassId,
      ogretmenId: teacherId,
      dersId: targetSubjectId,
      sinifId: targetClassId,
      weeklyHours: targetSubject?.weeklyHours || 0,
      haftalikSaat: targetSubject?.weeklyHours || 0,
    });
    setAssignments([
      ...assignments,
      {
        id: `local-${teacherId}-${targetSubjectId}-${targetClassId}`,
        teacherId: String(teacherId),
        subjectId: String(targetSubjectId),
        classId: String(targetClassId),
        weeklyHours: Number(targetSubject?.weeklyHours || 0),
      },
    ]);
    if (Array.isArray(emptyClassSubjects)) {
      setEmptyClassSubjects(emptyClassSubjects.filter(item => !(
        String(item.classId) === String(targetClassId) &&
        String(item.subjectId) === String(targetSubjectId)
      )));
    }
    refresh();
    toast.success('Atama kaydedildi');
  };

  const remove = async (id) => {
    const removed = assignments.find(item => String(item.id) === String(id));
    await axios.delete(`${API}/atamalar/${id}`);
    const nextAssignments = assignments.filter(item => String(item.id) !== String(id));
    setAssignments(nextAssignments);
    if (removed && Array.isArray(emptyClassSubjects)) {
      const stillAssigned = nextAssignments.some(item => (
        String(item.classId) === String(removed.classId) &&
        String(item.subjectId) === String(removed.subjectId)
      ));
      const alreadyEmpty = emptyClassSubjects.some(item => (
        String(item.classId) === String(removed.classId) &&
        String(item.subjectId) === String(removed.subjectId)
      ));
      const classSubject = classSubjects.find(item => (
        String(item.classId) === String(removed.classId) &&
        String(item.subjectId) === String(removed.subjectId)
      ));
      if (!stillAssigned && !alreadyEmpty && classSubject) {
        setEmptyClassSubjects([...emptyClassSubjects, classSubject]);
      }
    }
    toast.success('Atama silindi');
  };

  const openEmptyLessons = () => {
    setSelectedEmptyKey(emptyLessonRows[0]?.key || '');
    setEmptyOpen(true);
  };

  const goEmptyLesson = async (lesson = selectedEmptyLesson) => {
    if (!lesson) return toast.error('Boş ders yok');
    setSelectedEmptyKey(lesson.key);
    setSubjectId(String(lesson.subjectId));
    setClassId(String(lesson.classId));
    setEmptyOpen(false);
    toast.success('Boş ders seçildi');
  };

  return (
    <WindowModal title="Öğretmenlere Ders Atanması" width={1240} onClose={onClose}>
      <div className="hp-teacher-assign-layout">
        <div className="hp-teacher-left">
          <div className="hp-panel-title">Öğretmenler</div>
          <DenseTable headers={['No', 'Adı Soyadı', 'Branşı', 'Top.']} minWidth={420}>
            {teachers.map((item, index) => {
              const total = assignments
                .filter(row => String(row.teacherId) === String(item.id))
                .reduce((sum, row) => sum + Number(subjectMap[String(row.subjectId)]?.weeklyHours || row.weeklyHours || 0), 0);
              return (
                <tr
                  key={item.id}
                  className={String(item.id) === String(teacherId) ? 'selected' : ''}
                  onClick={() => setTeacherId(String(item.id))}
                >
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.branch || '-'}</td>
                  <td>{total || ''}</td>
                </tr>
              );
            })}
          </DenseTable>

          <div className="hp-panel-title">Öğretmenin Okuttuğu Dersler</div>
          <DenseTable headers={['Sınıf', 'Dersin Adı', 'Kısa Adı', 'H', '']} minWidth={420}>
            {currentRows.length === 0 ? (
              <tr><td colSpan={5} className="hp-empty-cell">{selectedTeacher?.name || '-'} için atama yok</td></tr>
            ) : currentRows.map(row => {
              const subject = subjectMap[String(row.subjectId)];
              return (
                <tr key={row.id}>
                  <td>{entityName(classMap, row.classId)}</td>
                  <td>{entityName(subjectMap, row.subjectId)}</td>
                  <td>{subject?.shortName || '-'}</td>
                  <td>{subject?.weeklyHours || row.weeklyHours || ''}</td>
                  <td><button className="hp-link-btn danger" onClick={() => remove(row.id)}>Sil</button></td>
                </tr>
              );
            })}
          </DenseTable>
        </div>

        <div className="hp-teacher-subjects">
          <div className="hp-panel-title">Dersler</div>
          <DenseTable headers={['Dersin Adı', 'HDS']} minWidth={320}>
            {subjects.map(item => (
              <tr
                key={item.id}
                className={String(item.id) === String(subjectId) ? 'selected' : ''}
                onClick={() => {
                  setSubjectId(String(item.id));
                  const firstSubjectClass = classSubjects.find(row => String(row.subjectId) === String(item.id));
                  setClassId(String(firstSubjectClass?.classId || classes[0]?.id || ''));
                }}
              >
                <td>{item.name}</td>
                <td>{item.weeklyHours || ''}</td>
              </tr>
            ))}
          </DenseTable>
        </div>

        <div className="hp-teacher-classes">
          <div className="hp-panel-title">Dersin Okutulduğu Sınıflar</div>
          <DenseTable headers={['No', 'Sınıf', 'Öğretmen']} minWidth={360}>
            {classRows.map((item, index) => {
              const rowAssignment = assignments.find(row => String(row.subjectId) === String(subjectId) && String(row.classId) === String(item.id));
              return (
                <tr
                  key={item.id}
                  className={String(item.id) === String(classId) ? 'selected' : ''}
                  onClick={() => setClassId(String(item.id))}
                  onDoubleClick={() => add(item.id)}
                >
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{rowAssignment ? entityName(teacherMap, rowAssignment.teacherId) : ''}</td>
                </tr>
              );
            })}
          </DenseTable>
        </div>
      </div>

      <div className="hp-teacher-assign-actions">
        <button className="hp-wide-btn" onClick={() => remove(currentRows[currentRows.length - 1]?.id)} disabled={currentRows.length === 0}>Sil</button>
        <button className="hp-wide-btn">Yazdır</button>
        <button className="hp-wide-btn" onClick={openEmptyLessons}>Boş Dersler</button>
        <button className="hp-wide-btn">Dersliklere Ders Atanması</button>
        <button className="hp-wide-btn">Yardım</button>
        <button className="hp-wide-btn" onClick={onClose}>Kapat</button>
      </div>

      <div className="hp-teacher-assign-status">
        <span>F1 = Yardım</span>
        <span>F2 = Yazdır</span>
        <span>F6 = Boş Dersler</span>
        <span>F7 = Sil</span>
        <span>Enter / Çift tık = Ders Ver</span>
        <strong>{selectedTeacher?.name || '-'} Toplam HDS: {teacherWeeklyTotal}</strong>
      </div>

      {emptyOpen && (
        <div className="hp-empty-lessons-dialog">
          <div className="hp-empty-lessons-window">
            <div className="hp-window-titlebar">
              <div><strong>Boş Dersler</strong></div>
              <button className="hp-icon-btn" type="button" onClick={() => setEmptyOpen(false)}>x</button>
            </div>
            <div className="hp-empty-lessons-body">
              <DenseTable headers={['Sınıf', 'Dersin Adı', 'HDS']} minWidth={420}>
                {emptyLessonRows.length === 0 ? (
                  <tr><td colSpan={3} className="hp-empty-cell">Atanmamış ders yok</td></tr>
                ) : emptyLessonRows.map(row => (
                  <tr
                    key={row.key}
                    className={row.key === selectedEmptyKey ? 'selected' : ''}
                    onClick={() => setSelectedEmptyKey(row.key)}
                    onDoubleClick={() => goEmptyLesson(row)}
                  >
                    <td>{row.className}</td>
                    <td>{row.subjectName}</td>
                    <td>{row.weeklyHours || ''}</td>
                  </tr>
                ))}
              </DenseTable>
              <div className="hp-empty-lessons-actions">
                <button className="hp-wide-btn" onClick={() => goEmptyLesson()}>Git</button>
                <button className="hp-wide-btn" onClick={() => setEmptyOpen(false)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </WindowModal>
  );
}

function ProgramListWindow({ onClose, setTimetableResult }) {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/liste`).then((response) => {
      const payload = getPayload(response);
      setPrograms(Array.isArray(payload) ? payload : asArray(payload.programs || payload.list || payload.items));
      if (!Array.isArray(payload) && payload.assignments) setTimetableResult(normalizeTimetable(payload));
    }).catch(() => toast.error('Program listesi alınamadı'));
  }, [setTimetableResult]);

  return (
    <WindowModal title="Programlar" width={860} onClose={onClose}>
      <DenseTable headers={['No', 'Program', 'Tarih', 'İşlem']} minWidth={620}>
        {programs.map((item, index) => (
          <tr key={item.id || index}>
            <td>{index + 1}</td>
            <td>{item.name || item.ad || `Program ${index + 1}`}</td>
            <td>{item.createdAt || item.tarih || '-'}</td>
            <td><button className="hp-link-btn" onClick={() => item.timetableResult && setTimetableResult(normalizeTimetable(item.timetableResult))}>Yükle</button></td>
          </tr>
        ))}
        {programs.length === 0 && <tr><td colSpan={4}>Kayıtlı program bulunamadı veya backend mevcut programı doğrudan döndürdü.</td></tr>}
      </DenseTable>
    </WindowModal>
  );
}

const styles = `
.hp-page { color:#111827; font-family: Tahoma, Arial, sans-serif; }
.pragmatic-page { min-height:calc(100vh - 96px); background:#d9d9d9; padding:0; }
.prag-shell { position:relative; min-height:760px; background:#f8f8f6; border:3px solid #1d222b; box-shadow:0 6px 18px rgba(0,0,0,.25); overflow:hidden; }
.prag-titlebar { height:28px; display:flex; align-items:center; gap:8px; padding:0 8px; background:linear-gradient(#55585c,#2f3339); color:#e8edf2; font-size:15px; font-weight:700; }
.prag-app-dot { width:17px; height:17px; display:inline-flex; align-items:center; justify-content:center; background:#dae8ff; color:#243a69; border:1px solid #8aa0bf; font-size:12px; }
.prag-window-controls { margin-left:auto; letter-spacing:8px; font-size:20px; line-height:1; }
.prag-content { display:grid; grid-template-columns:250px minmax(0,1fr); min-height:710px; padding:26px 20px 70px 18px; }
.prag-brand { display:flex; flex-direction:column; align-items:center; padding-top:10px; color:#111; text-align:center; }
.prag-asder-logo { width:132px; height:132px; object-fit:contain; margin-bottom:10px; filter:drop-shadow(0 10px 16px rgba(15,23,42,.16)); }
.prag-logo-text { font-size:36px; font-weight:900; letter-spacing:.2px; line-height:1.05; color:#2563eb; }
.prag-dna { margin-top:9px; max-width:210px; font-size:15px; letter-spacing:2px; text-transform:uppercase; color:#64748b; line-height:1.45; font-weight:800; }
.prag-version { color:#b03030; font-size:18px; font-weight:800; margin:4px 0 2px; }
.prag-brand a { color:#4f46e5; font-size:16px; text-decoration:underline; }
.prag-main { position:relative; min-width:0; }
.prag-top-row { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; min-height:115px; }
.prag-toolbar { display:flex; align-items:stretch; gap:8px; flex-wrap:wrap; }
.prag-toolbar-tile { width:106px; height:96px; border:2px solid #c9c9c9; background:linear-gradient(#f7f7f7,#dedede); color:#222; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; cursor:pointer; box-shadow:inset 0 1px 0 #fff; }
.prag-toolbar-tile.active { border-color:#4d87c8; box-shadow:inset 0 0 0 1px #97c2ef; }
.prag-toolbar-tile.muted { opacity:.55; }
.prag-toolbar-tile span { font-size:30px; color:#456a9c; }
.prag-toolbar-tile b { font-size:14px; line-height:1.1; text-align:center; white-space:normal; }
.prag-contact { width:116px; height:76px; border:2px solid #d0d0d0; background:linear-gradient(#fff,#dfdfdf); display:flex; align-items:center; justify-content:center; gap:8px; color:#222; font-size:26px; font-weight:800; cursor:pointer; }
.prag-contact span { font-size:16px; }
.prag-support { position:absolute; right:16px; top:54px; width:230px; height:48px; border:2px solid #ddd; border-radius:7px; background:#f5f5f5; color:#9a9a9a; font-size:15px; font-weight:900; opacity:.9; }
.prag-heading { margin:6px 0 0; text-align:center; color:#1d1d1d; font-size:26px; font-weight:900; text-decoration:underline; letter-spacing:.5px; }
.prag-heading span { font-weight:800; }
.prag-school { text-align:center; margin:20px 0 18px; font-size:22px; font-weight:900; letter-spacing:.7px; color:#161616; }
.prag-module-grid { display:grid; grid-template-columns:repeat(5, minmax(120px, 1fr)); column-gap:54px; row-gap:32px; align-items:start; padding:0 18px; }
.prag-module { min-height:150px; border:0; background:transparent; display:flex; flex-direction:column; align-items:center; justify-content:flex-start; gap:10px; color:#222; cursor:pointer; text-align:center; }
.prag-module:hover b { text-decoration:underline; }
.prag-module b { font-family:Georgia, 'Times New Roman', serif; font-size:18px; line-height:1.18; font-weight:800; text-shadow:0 1px 0 #fff; white-space:normal; }
.prag-module-icon { position:relative; width:112px; height:96px; display:flex; align-items:center; justify-content:center; border-radius:20px; background:linear-gradient(145deg,#f8fbff,#e7eef8); color:#2563eb; box-shadow:0 14px 26px rgba(15,23,42,.12), inset 0 1px 0 #fff; border:1px solid #dbe6f4; }
.prag-module-icon svg { width:54px; height:54px; filter:drop-shadow(0 5px 8px rgba(37,99,235,.18)); }
.prag-module-icon.class, .prag-module-icon.classSchedule { color:#0f9f8f; background:linear-gradient(145deg,#f5fffd,#dff7f3); }
.prag-module-icon.room, .prag-module-icon.roomSchedule { color:#64748b; background:linear-gradient(145deg,#fbfcfe,#e7ebf0); }
.prag-module-icon.teachers, .prag-module-icon.teacherSchedule, .prag-module-icon.assignment { color:#7c3aed; background:linear-gradient(145deg,#fbf8ff,#ede5ff); }
.prag-module-icon.daily, .prag-module-icon.duty { color:#e11d48; background:linear-gradient(145deg,#fff8fa,#ffe4eb); }
.prag-module-icon.classSubjects, .prag-module-icon.newTable { color:#0891b2; background:linear-gradient(145deg,#f3fcff,#dff6fb); }
.prag-module-icon.extra { color:#ea580c; background:linear-gradient(145deg,#fff9f2,#ffead2); }
.prag-module-icon.board { color:#b45309; background:linear-gradient(145deg,#fffaf0,#f7e5bd); }
.prag-module-icon.backup { color:#0284c7; background:linear-gradient(145deg,#f3fbff,#dcedfb); }
.prag-bottom-left { position:absolute; left:14px; bottom:14px; }
.prag-bottom-left button { width:102px; height:48px; border:2px solid #d0d0d0; background:linear-gradient(#f8f8f8,#dedede); color:#333; font-size:12px; font-weight:800; }
.prag-bottom-actions { position:absolute; right:16px; bottom:16px; display:flex; gap:14px; }
.prag-bottom-actions button { min-width:210px; height:48px; border:2px solid #bdbdbd; background:linear-gradient(#f7f7f7,#dbdbdb); color:#1d1d1d; font-size:16px; font-weight:900; cursor:pointer; }
.pragmatic-warning { position:absolute; left:280px; right:280px; top:160px; z-index:1; background:#fff5d6; border:1px solid #d1b456; color:#7a4b00; padding:6px 10px; font-size:12px; }

.prag-module-icon .block { position:absolute; width:28px; height:68px; border-radius:4px; transform:skewY(-18deg); box-shadow:0 3px 5px rgba(0,0,0,.16); }
.prag-module-icon .block.blue { left:16px; bottom:8px; background:#4aa3ff; }
.prag-module-icon .block.red { left:50px; bottom:14px; background:#ff6683; height:56px; }
.prag-module-icon .block.green { left:82px; bottom:26px; background:#18d4ac; height:62px; }
.prag-module-icon .block.cap { left:38px; top:0; width:70px; height:30px; background:#2c3b78; transform:rotate(10deg); border-radius:8px 8px 4px 4px; }
.prag-module-icon .board { position:absolute; left:24px; top:16px; width:70px; height:42px; background:#92c765; border:4px solid #805845; border-radius:4px; }
.prag-module-icon .board.small { top:12px; left:38px; width:60px; height:34px; }
.prag-module-icon .head { position:absolute; bottom:8px; width:25px; height:25px; background:#f7d6ad; border-radius:50%; box-shadow:0 22px 0 #59616c; }
.prag-module-icon .head.one { left:22px; }
.prag-module-icon .head.two { left:52px; }
.prag-module-icon .head.three { left:82px; }
.prag-module-icon .cube { position:absolute; width:42px; height:42px; background:#9aa4b2; transform:skewY(-25deg); box-shadow:inset 12px 0 #636e7e; }
.prag-module-icon .cube.a { left:18px; top:30px; }
.prag-module-icon .cube.b { left:50px; top:18px; background:#b7e25c; }
.prag-module-icon .cube.c { left:78px; top:28px; background:#ffd45a; }
.prag-module-icon .avatar { position:absolute; width:48px; height:48px; border-radius:50%; box-shadow:0 24px 0 -8px currentColor; }
.prag-module-icon .avatar.blue { left:36px; top:0; background:#79c9f8; color:#2483c6; }
.prag-module-icon .avatar.green { left:14px; top:38px; background:#daf08c; color:#99bf29; }
.prag-module-icon .avatar.red { left:68px; top:38px; background:#ff8b9a; color:#e5495f; }
.prag-module-icon .book { position:absolute; left:26px; top:24px; width:58px; height:54px; background:#2d9ee4; border-radius:5px; box-shadow:inset 0 -16px #ffd55b; }
.prag-module-icon .clock { position:absolute; right:18px; top:5px; width:42px; height:42px; border-radius:50%; background:#ffd94a; border:4px solid #f05a78; }
.prag-module-icon .teacher { position:absolute; left:14px; top:18px; width:26px; height:26px; background:#f6d2a5; border-radius:50%; box-shadow:0 26px 0 #72a5cb; }
.prag-module-icon .screen, .prag-module-icon .monitor { position:absolute; left:18px; top:18px; width:82px; height:56px; background:#31c9c7; border-radius:5px; box-shadow:0 9px 0 #4b6079; }
.prag-module-icon .teacher.mini, .prag-module-icon .person { position:absolute; right:18px; top:8px; width:20px; height:42px; background:#f4dc47; border-radius:10px 10px 3px 3px; }
.prag-module-icon .calendar { position:absolute; left:24px; top:12px; width:72px; height:68px; background:#8fd5dc; border-radius:5px; box-shadow:inset 0 16px #456c98; }
.prag-module-icon .calendar:after { content:''; position:absolute; inset:24px 8px 8px; background:repeating-linear-gradient(90deg,#fff 0 9px,transparent 9px 14px), repeating-linear-gradient(#fff 0 8px,transparent 8px 14px); opacity:.8; }
.prag-module-icon .building { position:absolute; left:20px; top:8px; width:80px; height:78px; background:#2e78bd; clip-path:polygon(50% 0,100% 22%,78% 100%,22% 100%,0 22%); }
.prag-module-icon .building:after { content:''; position:absolute; left:34px; top:22px; width:16px; height:56px; background:#9dccff; box-shadow:-24px -2px #71a7db,24px -2px #71a7db; }
.prag-module-icon .paper { position:absolute; left:20px; top:22px; width:76px; height:56px; background:#f1f5f9; border-radius:8px; box-shadow:inset 12px 0 #dbe7f3; }
.prag-module-icon .plus { position:absolute; right:18px; top:6px; width:36px; height:36px; background:#2fc9b4; border-radius:50%; color:#fff; }
.prag-module-icon .plus:before { content:'+'; position:absolute; inset:0; text-align:center; line-height:36px; font-size:28px; font-weight:900; }
.prag-module-icon .cap-mini { position:absolute; right:12px; bottom:8px; width:54px; height:38px; background:#344b79; clip-path:polygon(50% 0,100% 35%,50% 70%,0 35%); }
.prag-module-icon .math { position:absolute; left:20px; top:24px; width:78px; height:52px; color:#ff9a34; background:#eaf6ff; border-radius:4px; font-size:30px; font-weight:900; line-height:52px; }
.prag-module-icon .notice { position:absolute; left:20px; top:12px; width:78px; height:66px; background:#c58d50; transform:skewY(-15deg); box-shadow:inset 8px 8px #f4d48a; }
.prag-module-icon .cloud { position:absolute; left:24px; top:26px; width:70px; height:44px; background:#e7e8ea; border-radius:30px; }
.prag-module-icon .sync { position:absolute; left:32px; top:44px; width:58px; height:40px; border:10px solid #2b9ed8; border-left-color:#e94e4e; border-radius:50%; }

.hp-modal-backdrop, .hp-nested-modal { position:fixed; inset:0; z-index:1000; background:rgba(0,0,0,.04); display:flex; align-items:center; justify-content:center; padding:18px; }
.hp-nested-modal { z-index:1002; background:rgba(0,0,0,.12); }
.hp-window { width:100%; max-height:91vh; background:#dcdedb; border:4px solid #151b24; box-shadow:0 16px 38px rgba(0,0,0,.36); display:flex; flex-direction:column; overflow:hidden; }
.hp-window.small { max-width:900px; }
.hp-window-titlebar { min-height:27px; display:flex; align-items:center; justify-content:space-between; gap:10px; padding:0 7px; background:linear-gradient(#3f444a,#20252c); border-bottom:2px solid #11151b; color:#e8edf2; }
.hp-window-titlebar strong { color:#e8edf2; font-size:13px; text-transform:uppercase; letter-spacing:.2px; }
.hp-window-titlebar span { display:none; }
.hp-icon-btn, .hp-window-titlebar button { width:22px; height:22px; border:0; background:transparent; color:#e8edf2; cursor:pointer; font-weight:900; font-size:17px; }
.hp-window-body { padding:10px; overflow:auto; background:#dcdedb; }
.hp-window-grid { display:grid; gap:10px; }
.hp-window-grid.two { grid-template-columns:320px minmax(0,1fr); }
.hp-window-grid.two.narrow { grid-template-columns:250px minmax(0,1fr); }
.hp-window-grid.three { grid-template-columns:210px minmax(0,1fr) minmax(0,1fr); }
.hp-window-grid.settings { grid-template-columns:230px minmax(0,1fr); }
.hp-class-subjects { position:relative; display:grid; grid-template-columns:260px minmax(420px,1fr) 370px; grid-template-rows:minmax(520px,1fr) auto; gap:10px; align-items:stretch; }
.hp-class-col, .hp-assigned-col, .hp-subject-col { min-width:0; overflow:auto; background:#dcdedb; }
.hp-class-subjects .hp-table-wrap { max-height:535px; border:1px solid #b8bdc1; background:#f7f7f7; }
.hp-class-subjects .hp-table { background:#f7f7f7; }
.hp-class-subjects .hp-table th { position:sticky; top:0; z-index:1; background:#6d6d6d; color:#fff; border-color:#d7d7d7; height:28px; }
.hp-class-subjects .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.hp-class-subjects .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.hp-class-subjects .hp-table tr { cursor:pointer; }
.hp-class-subjects .hp-table tr.selected td { background:#4048f2 !important; color:#fff; }
.hp-class-subjects .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.hp-class-subjects .hp-link-btn { margin:0; white-space:nowrap; }
.hp-class-subject-actions { grid-column:1 / -1; display:grid; grid-template-columns:190px 190px 170px 270px 160px; justify-content:center; gap:16px; padding:0 8px; }
.hp-class-subject-actions .hp-wide-btn { min-height:38px; }
.hp-class-subject-actions .hp-wide-btn:disabled { opacity:.45; cursor:not-allowed; }
.hp-class-subject-status { grid-column:1 / -1; display:grid; grid-template-columns:180px 260px 130px 210px minmax(160px,1fr) minmax(180px,1fr); align-items:center; gap:10px; min-height:24px; padding:2px 4px 0; color:#57606a; font-size:12px; font-weight:800; }
.hp-class-subject-status strong { color:#2b3138; }
.hp-empty-cell { text-align:center !important; color:#6b7280 !important; background:#f8f8f8 !important; height:70px !important; }
.hp-copy-dialog { position:absolute; inset:0; z-index:3; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.06); }
.hp-copy-window { width:520px; max-width:calc(100vw - 48px); background:#dcdedb; border:4px solid #151b24; box-shadow:0 12px 28px rgba(0,0,0,.36); }
.hp-copy-body { display:grid; grid-template-columns:220px minmax(0,1fr); gap:12px; padding:10px; min-height:320px; }
.hp-copy-list .hp-table-wrap { max-height:300px; }
.hp-copy-summary { display:flex; flex-direction:column; justify-content:center; gap:18px; border:2px solid #b8bdc1; background:#ece9e6; padding:22px; color:#4b5563; font-size:13px; font-weight:800; }
.hp-copy-summary div { display:grid; grid-template-columns:1fr 110px; gap:16px; align-items:center; }
.hp-copy-summary strong { color:#384150; }
.hp-copy-summary small { color:#6b7280; font-size:12px; }
.hp-copy-summary .hp-wide-btn { align-self:flex-end; width:140px; min-height:36px; }
.hp-common-dialog { position:absolute; inset:0; z-index:4; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.08); }
.hp-common-window { width:940px; max-width:calc(100vw - 48px); max-height:calc(100vh - 92px); background:#dcdedb; border:4px solid #151b24; box-shadow:0 14px 32px rgba(0,0,0,.38); display:flex; flex-direction:column; overflow:hidden; }
.hp-common-body { display:grid; grid-template-columns:330px minmax(290px,1fr) 330px; gap:10px; padding:10px; min-height:590px; overflow:auto; }
.hp-common-left, .hp-common-middle, .hp-common-right { min-width:0; display:flex; flex-direction:column; gap:14px; }
.hp-common-left .hp-table-wrap:first-child { max-height:285px; }
.hp-common-left .hp-table-wrap:last-child { max-height:310px; }
.hp-common-middle .hp-table-wrap, .hp-common-right .hp-table-wrap { max-height:390px; min-height:300px; }
.hp-common-middle { justify-content:flex-start; padding-top:0; }
.hp-common-middle > .hp-wide-btn { align-self:center; width:150px; margin-top:4px; }
.hp-common-actions { margin-top:auto; display:flex; justify-content:flex-end; gap:24px; padding-top:14px; }
.hp-common-actions .hp-wide-btn { width:140px; min-height:38px; }
.hp-teacher-assign-layout { display:grid; grid-template-columns:minmax(420px,1.15fr) minmax(300px,.85fr) minmax(380px,1fr); gap:10px; min-height:590px; }
.hp-teacher-left, .hp-teacher-subjects, .hp-teacher-classes { min-width:0; display:flex; flex-direction:column; gap:14px; }
.hp-teacher-left .hp-table-wrap:first-of-type { max-height:265px; }
.hp-teacher-left .hp-table-wrap:last-of-type { max-height:330px; min-height:240px; }
.hp-teacher-subjects .hp-table-wrap, .hp-teacher-classes .hp-table-wrap { max-height:610px; min-height:420px; }
.hp-teacher-assign-layout .hp-table-wrap { border:1px solid #b8bdc1; background:#f7f7f7; }
.hp-teacher-assign-layout .hp-table th { position:sticky; top:0; z-index:1; background:#6d6d6d; color:#fff; border-color:#d7d7d7; height:28px; }
.hp-teacher-assign-layout .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.hp-teacher-assign-layout .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.hp-teacher-assign-layout .hp-table tr { cursor:pointer; }
.hp-teacher-assign-layout .hp-table tr.selected td { background:#4048f2 !important; color:#fff; }
.hp-teacher-assign-layout .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.hp-teacher-assign-actions { display:grid; grid-template-columns:150px 150px 170px 260px 150px 150px; justify-content:center; gap:18px; padding:12px 8px 0; }
.hp-teacher-assign-actions .hp-wide-btn { min-height:38px; }
.hp-teacher-assign-actions .hp-wide-btn:disabled { opacity:.45; cursor:not-allowed; }
.hp-teacher-assign-status { display:grid; grid-template-columns:140px 140px 170px 120px 210px minmax(200px,1fr); align-items:center; gap:10px; min-height:24px; padding:8px 4px 0; color:#57606a; font-size:12px; font-weight:800; }
.hp-teacher-assign-status strong { color:#2b3138; }
.hp-empty-lessons-dialog { position:absolute; inset:0; z-index:5; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.08); }
.hp-empty-lessons-window { width:440px; max-width:calc(100vw - 48px); background:#dcdedb; border:4px solid #151b24; box-shadow:0 12px 28px rgba(0,0,0,.38); overflow:hidden; }
.hp-empty-lessons-body { padding:10px; }
.hp-empty-lessons-body .hp-table-wrap { max-height:420px; min-height:320px; border:1px solid #b8bdc1; background:#f7f7f7; }
.hp-empty-lessons-body .hp-table th { position:sticky; top:0; z-index:1; background:#6d6d6d; color:#fff; border-color:#d7d7d7; height:28px; }
.hp-empty-lessons-body .hp-table td { background:#c8c8c8; border-color:#efefef; color:#444; height:25px; padding:3px 7px; font-size:12px; font-weight:700; }
.hp-empty-lessons-body .hp-table tr:nth-child(even) td { background:#bfbfbf; }
.hp-empty-lessons-body .hp-table tr { cursor:pointer; }
.hp-empty-lessons-body .hp-table tr.selected td { background:#4048f2 !important; color:#fff; }
.hp-empty-lessons-body .hp-table tr:hover td { background:#737bff !important; color:#fff; }
.hp-empty-lessons-actions { display:flex; justify-content:flex-end; gap:10px; padding-top:10px; }
.hp-empty-lessons-actions .hp-wide-btn { width:120px; min-height:36px; }
.hp-form-card, .hp-transfer-box, .hp-mini-panel { background:#dcdedb; border:0; box-shadow:none; padding:10px; }
.hp-field { display:grid; grid-template-columns:118px 1fr; align-items:center; gap:8px; margin-bottom:8px; }
.hp-field span { color:#222; font-size:12px; font-weight:600; }
.hp-inline-form select, .hp-field input, .hp-field select, .hp-table input { width:100%; border:2px solid #a9b1b8; background:#edf2f4; color:#111827; padding:5px 7px; font-size:12px; outline:none; border-radius:2px; min-height:28px; }
.hp-row-actions { display:grid; gap:13px; width:190px; margin:34px auto 0; }
.hp-row-actions button, .hp-wide-btn, .hp-actions-panel button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#cfd3d5); color:#222; font-size:13px; font-weight:800; cursor:pointer; box-shadow:inset 0 1px 0 #fff; border-radius:5px; }
.hp-row-actions .danger, .hp-link-btn.danger { color:#b91c1c; }
.hp-table-wrap, .hp-grid-scroll { overflow:auto; width:100%; }
.hp-table { width:100%; border-collapse:collapse; background:#cfe7f1; }
.hp-table th, .hp-table td { border:2px solid #8aa0ad; padding:5px 7px; font-size:12px; text-align:left; vertical-align:middle; height:26px; white-space:nowrap; }
.hp-table th { background:#3d4855; color:#fff; font-weight:900; text-align:center; }
.hp-table tr:nth-child(even) td { background:#d8edf5; }
.hp-table tr.selected td, .hp-table tr:hover td { background:#062f78 !important; color:#fff; }
.hp-link-btn { border:0; background:transparent; color:#003f9e; font-size:12px; font-weight:900; cursor:pointer; margin-right:8px; }
.hp-panel-title { display:flex; align-items:center; justify-content:center; color:#222; font-size:13px; font-weight:900; text-transform:uppercase; margin-bottom:8px; }
.hp-list-box { padding:0; background:#dcdedb; border:0; overflow:auto; max-height:560px; }
.hp-list-box button { width:100%; display:flex; align-items:center; justify-content:space-between; gap:8px; border:1px solid #a9b1b8; background:#cfe7f1; padding:6px 8px; margin:0 0 2px; color:#111; cursor:pointer; text-align:left; font-size:12px; }
.hp-list-box button.active { background:#062f78; color:#fff; }
.hp-list-box button span { color:inherit; opacity:.8; }
.hp-settings-head { display:grid; grid-template-columns:1fr 92px 92px 74px; gap:8px; align-items:end; margin-bottom:10px; }
.hp-transfer-row { display:flex; align-items:center; justify-content:space-between; gap:8px; border:1px solid #9aa8b2; background:#cfe7f1; padding:5px 7px; margin-bottom:2px; font-size:12px; font-weight:700; }
.hp-transfer-row button { border:1px solid #b7bbbf; background:linear-gradient(#f4f5f5,#d0d4d6); padding:4px 8px; cursor:pointer; font-size:11px; font-weight:900; }
.hp-assignment-layout { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; margin-bottom:10px; }
.hp-check { display:flex; align-items:center; gap:6px; font-size:12px; font-weight:800; color:#222; }
.hp-muted { color:#60656a; font-size:12px; margin:0; }
.hp-cell-summary { display:grid; gap:2px; margin-bottom:8px; }
.hp-cell-summary strong { font-size:12px; }
.hp-cell-summary span, .hp-cell-summary small { color:#4b5563; font-size:12px; }
.hp-inline-form { display:grid; grid-template-columns:1fr; gap:5px; }

.prag-program-tabs { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:8px; }
.prag-program-tabs button { height:48px; border:2px solid #bfc4c8; background:linear-gradient(#ededed,#cfd3d4); color:#333; font-size:13px; font-weight:800; cursor:pointer; }
.prag-program-tabs button.active { background:#e8ecef; border-color:#7d8e9e; box-shadow:inset 0 0 0 1px #fff; }
.prag-program-layout { display:grid; grid-template-columns:210px minmax(0,1fr); gap:8px; }
.prag-program-main { min-width:0; }
.prag-program-main.full { display:flex; flex-direction:column; gap:8px; }
.prag-program-list { max-height:500px; }
.prag-program-bottom.wide { display:grid; grid-template-columns:360px minmax(0,1fr) 180px; gap:8px; margin-top:0; align-items:start; }
.prag-program-bottom.wide .hp-table-wrap { max-height:260px; min-height:190px; border:1px solid #9aa8b2; background:#fff; }
.prag-program-bottom.wide .prag-program-list .hp-table-wrap { max-height:260px; }
.prag-program-side { display:flex; flex-direction:column; gap:8px; min-width:0; }
.prag-color-legend { display:grid; grid-template-columns:42px 1fr; border:1px solid #9aa8b2; background:#fff; }
.prag-color-legend div { display:contents; }
.prag-color-legend b, .prag-color-legend span { min-height:28px; border-bottom:1px solid #d1d5db; display:flex; align-items:center; padding:3px 8px; font-size:12px; font-weight:900; }
.prag-color-legend b { justify-content:center; border-right:1px solid #d1d5db; }
.prag-side-actions { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.prag-side-actions button { min-height:34px; border:1px solid #c4c8cc; background:linear-gradient(#f4f5f5,#d7dbde); color:#8a8f96; font-weight:900; border-radius:3px; }
.prag-time-row { display:grid; grid-template-columns:1fr 52px; gap:4px; }
.prag-time-row span, .prag-counts strong { min-height:28px; border:2px solid #c4c8cc; background:#f6f5f2; display:flex; align-items:center; justify-content:center; font-weight:900; color:#4b5563; }
.prag-counts { display:grid; gap:5px; }
.prag-counts label { display:grid; grid-template-columns:1fr 62px; align-items:center; gap:6px; font-size:12px; font-weight:900; color:#4b5563; }
.prag-selected-row { display:grid; grid-template-columns:minmax(0,1fr) 220px; gap:8px; align-items:stretch; }
.prag-selected-row .hp-table-wrap { max-height:58px; overflow:hidden; }
.prag-selected-row strong { display:flex; align-items:center; justify-content:center; background:#dcdedb; color:#222; font-size:13px; font-weight:900; }
.prag-program-actions { display:grid; grid-template-columns:repeat(9, minmax(100px, 1fr)); gap:8px; margin-top:8px; }
.prag-program-actions button { min-height:40px; border:1px solid #c4c8cc; background:linear-gradient(#f9f9f8,#d4d8db); color:#222; font-size:13px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; }
.prag-program-actions button:disabled { color:#9ca3af; background:linear-gradient(#f2f2f2,#d9dcde); cursor:not-allowed; }
.prag-program-shortcuts { display:grid; grid-template-columns:1fr 1fr 1fr; align-items:center; min-height:24px; margin-top:4px; border-top:1px solid #b9bec3; color:#59636e; font-size:12px; font-weight:800; }
.prag-program-shortcuts span { padding:3px 8px; border-right:1px solid #c8ccd0; }
.prag-lesson-menu { position:fixed; z-index:1010; width:210px; background:#d8d4cf; border:3px solid #22272f; box-shadow:0 10px 24px rgba(0,0,0,.42); padding:4px 0; }
.prag-lesson-menu button { width:100%; min-height:28px; border:0; border-bottom:1px solid #b6b0aa; background:transparent; color:#2b2b2b; text-align:left; padding:5px 14px; font-size:12px; font-weight:800; text-transform:uppercase; cursor:pointer; }
.prag-lesson-menu button:hover, .prag-lesson-menu button.active { background:#a04925; color:#fff; }
.hp-placement-window { max-width:520px !important; }
.hp-placement-window .hp-form-card { padding:16px; }
.hp-auto-settings-window { max-width:680px !important; }
.hp-auto-settings-body { display:grid; grid-template-columns:minmax(0,1fr) 190px 130px; gap:14px; padding:14px; background:#ebe8e4; }
.hp-auto-settings-form { display:grid; gap:14px; }
.hp-auto-settings-form label { display:grid; grid-template-columns:minmax(0,1fr) 48px; gap:10px; align-items:center; color:#4b4b4b; font-size:13px; font-weight:700; }
.hp-auto-settings-form input, .hp-auto-settings-form select { width:48px; height:26px; border:2px solid #b9b9b9; background:#f8f8f8; color:#222; text-align:center; font-size:13px; font-weight:800; }
.hp-auto-settings-days { display:flex; flex-direction:column; gap:7px; padding:4px 0; }
.hp-auto-settings-days strong { color:#333; font-size:13px; text-transform:uppercase; }
.hp-auto-settings-days label { display:grid; grid-template-columns:18px 1fr 48px; align-items:center; gap:6px; color:#333; font-size:12px; font-weight:800; }
.hp-auto-settings-days input[type="number"] { width:48px; height:24px; border:1px solid #b9b9b9; background:#fff; text-align:center; font-weight:800; }
.hp-auto-settings-actions { display:flex; flex-direction:column; gap:14px; padding-top:8px; }
.hp-auto-settings-actions button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f9f9f8,#d4d8db); color:#222; font-size:13px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; }
.hp-auto-settings-actions button:first-child { background:linear-gradient(#ffb07a,#ef8b52); }
.hp-print-window { max-width:430px !important; }
.hp-print-body { display:grid; grid-template-columns:1fr 170px; gap:22px; padding:14px; background:#ebe8e4; }
.hp-print-options { display:grid; gap:20px; }
.hp-print-group { display:grid; gap:18px; min-height:102px; padding:18px 12px; background:#f2f0ec; border:2px solid #d3d0cc; }
.hp-print-group label { display:flex; align-items:center; gap:9px; color:#333; font-size:13px; font-weight:900; }
.hp-print-group input { width:16px; height:16px; accent-color:#e46f3f; }
.hp-print-actions { display:flex; flex-direction:column; justify-content:flex-start; gap:16px; padding-top:8px; }
.hp-print-actions button { min-height:38px; border:1px solid #c4c8cc; background:linear-gradient(#f9f9f8,#d4d8db); color:#222; font-size:13px; font-weight:900; cursor:pointer; border-radius:5px; box-shadow:inset 0 1px 0 #fff; }
.hp-schedule-grid { width:100%; min-width:820px; border-collapse:collapse; table-layout:fixed; background:#cfe7f1; }
.hp-schedule-grid th, .hp-schedule-grid td { border:2px solid #6e8799; padding:4px; height:61px; vertical-align:top; }
.hp-schedule-grid th { background:#77b6e5; color:#17324c; font-size:12px; font-weight:900; text-align:left; }
.hp-schedule-grid .sticky-day { width:88px; position:sticky; left:0; z-index:2; }
.hp-schedule-grid td.inactive { background:#bfc7c9; }
.hp-schedule-grid td.light { background:#c8e8c4; }
.hp-schedule-grid td.dark { background:#161b24; color:#fff; }
.hp-schedule-grid td.availability-green { background:#34d399 !important; color:#052e1b; box-shadow:inset 0 0 0 3px #047857; cursor:pointer; }
.hp-schedule-grid td.availability-black { background:#020617 !important; color:#fff; box-shadow:inset 0 0 0 3px #475569; cursor:not-allowed; }
.hp-schedule-grid td.availability-red { background:#ef4444 !important; color:#fff; box-shadow:inset 0 0 0 3px #991b1b; cursor:not-allowed; }
.hp-lesson.pragmatic-lesson { position:relative; min-height:48px; border:0; background:transparent; color:inherit; padding:3px; display:flex; flex-direction:column; gap:1px; cursor:grab; overflow:hidden; }
.hp-lesson.pragmatic-lesson b, .hp-lesson.pragmatic-lesson span, .hp-lesson.pragmatic-lesson small { font-size:11px; line-height:1.08; color:inherit; font-weight:900; text-transform:uppercase; }
.lesson-lock { position:absolute; right:2px; bottom:2px; font-size:9px; opacity:.8; }
.prag-program-bottom { display:grid; grid-template-columns:minmax(0,1fr) 290px; gap:8px; margin-top:8px; }
.prag-statusbar { display:flex; justify-content:space-between; gap:10px; padding:5px 4px 0; color:#333; font-size:12px; font-weight:800; }
.hp-unavailable button { width:100%; border:1px solid #9aa8b2; background:#c8e8c4; color:#064e3b; padding:4px; font-size:11px; font-weight:900; cursor:pointer; }
.hp-unavailable button.closed { background:#161b24; color:#fff; }
.hp-unavailable td.inactive { background:#bfc7c9; }
.hp-loading { min-height:420px; display:flex; align-items:center; justify-content:center; color:#333; font-weight:900; }

@media (max-width: 1200px) {
  .prag-content { grid-template-columns:200px 1fr; }
  .prag-logo-text { font-size:38px; }
  .prag-module-grid { grid-template-columns:repeat(4, minmax(110px,1fr)); gap:24px; }
  .prag-module b { font-size:16px; }
  .prag-toolbar-tile { width:92px; }
}
@media (max-width: 900px) {
  .prag-shell { min-height:auto; }
  .prag-content, .hp-window-grid.two, .hp-window-grid.two.narrow, .hp-window-grid.three, .hp-window-grid.settings, .hp-assignment-layout, .hp-teacher-assign-layout, .prag-program-layout, .prag-program-bottom, .prag-program-bottom.wide, .prag-selected-row { grid-template-columns:1fr; }
  .prag-program-actions, .prag-program-shortcuts { grid-template-columns:1fr; }
  .hp-class-subjects { grid-template-columns:1fr; grid-template-rows:auto; }
  .hp-class-subject-actions, .hp-class-subject-status { grid-column:auto; grid-template-columns:1fr; }
  .hp-teacher-assign-actions, .hp-teacher-assign-status { grid-template-columns:1fr; }
  .hp-copy-body { grid-template-columns:1fr; }
  .hp-copy-summary div { grid-template-columns:1fr; gap:4px; }
  .hp-common-body { grid-template-columns:1fr; min-height:auto; }
  .prag-brand { display:none; }
  .prag-module-grid { grid-template-columns:repeat(2, minmax(120px,1fr)); padding:0; }
  .prag-bottom-actions { position:static; padding:12px; flex-direction:column; }
  .prag-bottom-left, .prag-support { display:none; }
  .hp-settings-head { grid-template-columns:1fr; }
}
`;
